import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db, storage, auth, rtdb } from '../firebase';
import { doc, updateDoc, getDoc, setDoc, collection, addDoc, deleteField, getDocs, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ref as rtdbRef, onValue, query as rtdbQuery, limitToLast, orderByChild, equalTo } from 'firebase/database';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaDollarSign, FaCheckCircle, FaTimesCircle, FaBolt, FaUser, FaKey, FaTimes, FaEnvelope, FaPhone, FaBuilding, FaGlobe, FaCreditCard, FaSpinner, FaGift, FaFileDownload, FaEdit, FaSearch } from 'react-icons/fa';
import dayjs from 'dayjs';
import './TakeoverDetail.css';

const PROXY_BASE_URL = 'https://vue-admin-proxy.fly.dev';
const PERION_API_BRIDGE_BASE = 'https://perion-api-bridge.fly.dev';
const PHOTO_OP_DEFAULT_PLAYS = 20;
const LIVE_BID_LABELS = [
  { key: 'playouts', name: 'Playouts', color: '#22c55e' },
  { key: 'pending', name: 'Pending', color: '#facc15' },
  { key: 'losses', name: 'Losses', color: '#f43f5e' },
  { key: 'noBids', name: 'No Bids', color: '#f59e0b' },
  { key: 'bids', name: 'Bids', color: '#3b82f6' },
];

const EMPTY_LIVE_BID_STATS = {
  playouts: 0,
  pending: 0,
  losses: 0,
  noBids: 0,
  bids: 0,
  lossReasons: [],
  noBidReasons: [],
  source: 'live',
};

const parseLiveEventTs = (raw) => {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw > 0 && raw < 1e12 ? raw * 1000 : raw;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n < 1e12 ? n * 1000 : n;
  const parsed = Date.parse(String(raw));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getTakeoverArchiveIds = (takeover, dspCampaign) => (
  [...new Set([
    takeover?.takeoverDspCampaignId,
    dspCampaign?.id,
    dspCampaign?.dspCampaignId,
    takeover?.id,
    takeover?.takeoverDspCampaignId ? `warmup-${takeover.takeoverDspCampaignId}` : '',
  ].map((id) => String(id || '').trim()).filter(Boolean))]
);

const topReasonList = (map, limit = 5) => (
  Object.values(map || {})
    .sort((a, b) => (b.count || 0) - (a.count || 0))
    .slice(0, limit)
);

const liveBidStatsEqual = (a, b) => (
  a?.playouts === b?.playouts &&
  a?.pending === b?.pending &&
  a?.losses === b?.losses &&
  a?.noBids === b?.noBids &&
  a?.bids === b?.bids &&
  a?.source === b?.source &&
  JSON.stringify(a?.lossReasons || []) === JSON.stringify(b?.lossReasons || []) &&
  JSON.stringify(a?.noBidReasons || []) === JSON.stringify(b?.noBidReasons || [])
);

const getFlipCountFromType = (flipType) => {
  const match = String(flipType || '').match(/(\d+)\s*flips?/i);
  return match ? Number(match[1]) : 0;
};
const PULSEFLOW_API = 'https://pulseflow.vuebillboards.com';

const parsePulseflowTimestamp = (rec) => {
  if (!rec || typeof rec !== 'object') return 0;
  return Number(
    rec.createdAtMs ||
      rec.created_at_ms ||
      rec.lastSeenAtMs ||
      rec.timestamp ||
      (rec.created_at ? Date.parse(rec.created_at) : 0) ||
      0
  ) || 0;
};

const fetchPulseflowPlayStats = async (campaignIds) => {
  const ids = [...new Set((campaignIds || []).map((id) => String(id || '').trim()).filter(Boolean))];
  const empty = { plays: 0, spend: 0, impressions: 0, lastPlayAt: null, sourceId: '' };
  if (!ids.length) return empty;

  const perId = await Promise.all(ids.map(async (id) => {
    const qs = `campaignId=${encodeURIComponent(id)}`;
    const stats = { plays: 0, spend: 0, impressions: 0, lastPlayAt: null, sourceId: id };
    try {
      const [summaryRes, recordsRes] = await Promise.all([
        fetch(`${PULSEFLOW_API}/api/archive/billing/summary?${qs}`),
        fetch(`${PULSEFLOW_API}/api/archive/billing/records?${qs}&limit=50&page=1`)
      ]);
      if (summaryRes.ok) {
        const summary = await summaryRes.json();
        stats.plays = Number(summary.totalRecords || summary.totalPlayouts || 0);
        stats.spend = Number(summary.totalBilledDollars || summary.totalSpendDollars || 0);
        stats.impressions = Number(summary.totalImpressions || 0);
      }
      if (recordsRes.ok) {
        const data = await recordsRes.json();
        const rows = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
        const latest = rows.reduce((max, rec) => Math.max(max, parsePulseflowTimestamp(rec)), 0);
        if (latest) stats.lastPlayAt = latest;
        if (!stats.plays && rows.length) stats.plays = rows.length;
      }
    } catch (e) {
      console.warn('Pulseflow play stats failed for', id, e);
    }
    return stats;
  }));

  return perId.reduce((best, cur) => {
    if (!cur) return best;
    const betterCount = cur.plays > best.plays || (cur.plays === best.plays && cur.spend > best.spend);
    if (betterCount) {
      return {
        ...cur,
        lastPlayAt: cur.lastPlayAt || best.lastPlayAt
      };
    }
    if ((cur.lastPlayAt || 0) > (best.lastPlayAt || 0)) {
      return { ...best, lastPlayAt: cur.lastPlayAt };
    }
    return best;
  }, empty);
};

const fetchFirestoreWinsFallback = async (campaignIds) => {
  const ids = [...new Set((campaignIds || []).map((id) => String(id || '').trim()).filter(Boolean))];
  let plays = 0;
  let lastPlayAt = null;
  for (const id of ids) {
    try {
      const snap = await getDocs(collection(db, 'takeovers', id, 'wins'));
      plays = Math.max(plays, snap.size);
      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const ts = Number(
          data.createdAtMs ||
            data.timestamp ||
            (data.createdAt?.toMillis ? data.createdAt.toMillis() : 0) ||
            (data.created_at ? Date.parse(data.created_at) : 0) ||
            0
        );
        if (ts && ts > (lastPlayAt || 0)) lastPlayAt = ts;
      });
    } catch {
      // Old wins subcollection is optional; ignore missing paths
    }
  }
  return { plays, lastPlayAt };
};

const parseTakeoverDateInput = (value) => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).format('YYYY-MM-DD');
  }
  if (typeof value?.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? dayjs(d).format('YYYY-MM-DD') : '';
  }
  if (typeof value === 'object' && typeof value?.seconds === 'number') {
    const d = new Date(value.seconds * 1000);
    return Number.isNaN(d.getTime()) ? '' : dayjs(d).format('YYYY-MM-DD');
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : dayjs(d).format('YYYY-MM-DD');
};

const formatHourLabel = (hour) => {
  const h = ((Number(hour) % 24) + 24) % 24;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:00 ${suffix}`;
};

const parseHourFromSlot = (slot) => {
  if (typeof slot === 'number' && Number.isFinite(slot)) {
    const h = Math.trunc(slot);
    return h >= 0 && h <= 23 ? h : null;
  }
  const s = String(slot || '').trim();
  if (/^\d{1,2}$/.test(s)) {
    const n = Number(s);
    return n >= 0 && n <= 23 ? n : null;
  }
  const match = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
  if (!match) return null;
  let h = Number(match[1]);
  const mer = (match[3] || '').toUpperCase();
  if (mer === 'PM' && h < 12) h += 12;
  if (mer === 'AM' && h === 12) h = 0;
  return h >= 0 && h <= 23 ? h : null;
};

const uniqueSortedHours = (hours) => (
  [...new Set(
    (hours || [])
      .map(Number)
      .filter((h) => Number.isFinite(h) && h >= 0 && h <= 23)
      .map((h) => Math.trunc(h))
  )].sort((a, b) => a - b)
);

const getSelectedHoursFromTakeover = (t) => {
  if (Array.isArray(t?.selectedHours) && t.selectedHours.length > 0) {
    return uniqueSortedHours(t.selectedHours);
  }
  if (Array.isArray(t?.timeSlots) && t.timeSlots.length > 0) {
    return uniqueSortedHours(t.timeSlots.map(parseHourFromSlot).filter((h) => h != null));
  }
  return [];
};

const getTimeSlotsFromTakeover = (t) => {
  if (Array.isArray(t?.timeSlots) && t.timeSlots.length > 0) return [...t.timeSlots];
  return getSelectedHoursFromTakeover(t).map(formatHourLabel);
};

const getCreativeDetailsInfo = (t) => {
  const info = t?.creativeDetailsInfo;
  if (!info || typeof info !== 'object') return null;
  const urls = [];
  if (typeof info.assetUrl === 'string' && info.assetUrl.trim()) urls.push(info.assetUrl.trim());
  if (Array.isArray(info.assetUrls)) {
    info.assetUrls.forEach((u) => {
      if (typeof u === 'string' && u.trim()) urls.push(u.trim());
    });
  }
  const uniqueUrls = [...new Set(urls)];
  const copyText = String(info.creativeCopy || '').trim();
  const detailsText = String(info.customDesignDetails || '').trim();
  const brief = copyText || detailsText;
  const extraBrief = copyText && detailsText && detailsText !== copyText ? detailsText : '';
  if (!uniqueUrls.length && !brief && !info.creativeStatus && !info.customDesign) return null;
  return { ...info, uniqueUrls, brief, extraBrief };
};

const sanitizeZipBaseName = (value) => {
  const cleaned = String(value || '').trim().replace(/[^\w.\-]+/g, '_').replace(/^_+|_+$/g, '');
  return cleaned || 'submitted-assets';
};

const ADMIN_API_HOSTS = new Set([
  'backend.vuebillboards.com',
  'hub.vuebillboards.com',
  'vue-admin-proxy.fly.dev',
]);

const getAdminApiBase = () => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    if (ADMIN_API_HOSTS.has(window.location.hostname)) {
      return String(window.location.origin).replace(/\/$/, '');
    }
  }
  return String(process.env.REACT_APP_API_BASE || PROXY_BASE_URL).replace(/\/$/, '');
};

const TakeoverDetail = ({ takeover, refreshTakeover, onClose }) => {
  const [status, setStatus] = useState(takeover?.status || '');
  const [editDate, setEditDate] = useState(parseTakeoverDateInput(takeover?.date));
  const [editTimeSlots, setEditTimeSlots] = useState(getTimeSlotsFromTakeover(takeover));
  const [editSelectedHours, setEditSelectedHours] = useState(getSelectedHoursFromTakeover(takeover));
  const [previewCreativeUrl, setPreviewCreativeUrl] = useState('');
  const [creativeDownloadLoading, setCreativeDownloadLoading] = useState(false);
  const [creativeDownloadProgress, setCreativeDownloadProgress] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  
  // Sign Editing State
  const [isEditingSign, setIsEditingSign] = useState(false);
  const [allSigns, setAllSigns] = useState([]);
  const [signSearchTerm, setSignSearchTerm] = useState('');
  const [signSearchLoading, setSignSearchLoading] = useState(false);

  // DSP takeover state
  const [dspCampaign, setDspCampaign] = useState(null);
  const [dspCampaignLoading, setDspCampaignLoading] = useState(false);
  const [dspCreativeMetaById, setDspCreativeMetaById] = useState({}); // creativeDocId -> { dspCreativeId, moderationStatus, statuses }
  const [dspCreativeStatusByDspId, setDspCreativeStatusByDspId] = useState({}); // dspCreativeId -> { global_approval_state, label, approvals }
  const [dspSubmittingCreativeId, setDspSubmittingCreativeId] = useState('');
  const [warmupLoading, setWarmupLoading] = useState(false);
  const [swapCreativeOpen, setSwapCreativeOpen] = useState(false);
  const [swapOldCreativeId, setSwapOldCreativeId] = useState('');
  const [swapNewCreativeId, setSwapNewCreativeId] = useState('');
  const [swapCreativeLoading, setSwapCreativeLoading] = useState(false);
  const [swapCreativeError, setSwapCreativeError] = useState('');
  const [createCreativeOpen, setCreateCreativeOpen] = useState(false);
  const [createCreativeFile, setCreateCreativeFile] = useState(null);
  const [createCreativeDimensions, setCreateCreativeDimensions] = useState('');
  const [createCreativeName, setCreateCreativeName] = useState('');
  const [createCreativeLoading, setCreateCreativeLoading] = useState(false);
  const [createCreativeError, setCreateCreativeError] = useState('');

  // IAB Category selection (match CreateCampaignView.vue behavior)
  const [iabCategories, setIabCategories] = useState([]); // [{ code, tier, name }]
  const [iabCategoriesLoading, setIabCategoriesLoading] = useState(false);
  const [iabCategoriesError, setIabCategoriesError] = useState('');
  const [iabCategoryByFormat, setIabCategoryByFormat] = useState({}); // normalizedFormatKey -> code
  const [showIabPickerModal, setShowIabPickerModal] = useState(false);
  const [pendingIabCode, setPendingIabCode] = useState('');
  const [pendingIabNormalizedKey, setPendingIabNormalizedKey] = useState('');
  const [iabSearchTerm, setIabSearchTerm] = useState('');
  const [pendingDspSubmitCreative, setPendingDspSubmitCreative] = useState(null);
  const iabSearchInputRef = useRef(null);

  // "Creative already exists" choice modal (explicit buttons vs confirm popup)
  const [existsChoiceOpen, setExistsChoiceOpen] = useState(false);
  const [existsChoiceLoading, setExistsChoiceLoading] = useState(false);
  const [existsChoiceTitle, setExistsChoiceTitle] = useState('Creative Already Exists');
  const [existsChoiceBody, setExistsChoiceBody] = useState('');
  const existsChoiceResolverRef = useRef(null); // resolves to 'link' | 'resubmit' | 'cancel'

  // DSP schedule editing (bidding schedule)
  const [dspScheduleOpen, setDspScheduleOpen] = useState(false);
  const [dspScheduleLoading, setDspScheduleLoading] = useState(false);
  const [dspScheduleError, setDspScheduleError] = useState('');
  const [dspEditStartDateTime, setDspEditStartDateTime] = useState(''); // datetime-local string
  const [dspEditEndDateTime, setDspEditEndDateTime] = useState(''); // datetime-local string
  const [dspEditDays, setDspEditDays] = useState([0, 1, 2, 3, 4, 5, 6]); // 0..6
  const [dspEditStartTime, setDspEditStartTime] = useState('00:00'); // HH:MM
  const [dspEditEndTime, setDspEditEndTime] = useState('23:59'); // HH:MM
  const [dspApplyToWarmup, setDspApplyToWarmup] = useState(false);

  // Advertiser URL/domain for DSP creative submit + bidder-facing campaign fields
  const [dspAdvertiserUrlDraft, setDspAdvertiserUrlDraft] = useState('');
  const [dspAdvertiserUrlSaving, setDspAdvertiserUrlSaving] = useState(false);

  const [dspBudgetDraft, setDspBudgetDraft] = useState('');
  const [dspBudgetSaving, setDspBudgetSaving] = useState(false);

  // Activation & plays state
  const [isActivated, setIsActivated] = useState(!!takeover?.activated);
  const [activationLoading, setActivationLoading] = useState(false);
  const [activationSuccess, setActivationSuccess] = useState(false);
  const [activationError, setActivationError] = useState('');
  const [totalWins, setTotalWins] = useState(0);
  const [totalWinsLoading, setTotalWinsLoading] = useState(false);
  const [playSpend, setPlaySpend] = useState(0);
  const [lastPlayAt, setLastPlayAt] = useState(null);
  const [liveBidStats, setLiveBidStats] = useState(EMPTY_LIVE_BID_STATS);
  const [hasBidChartData, setHasBidChartData] = useState(false);
  const playsLoadedRef = useRef(false);
  const [copyPhotoOpSuccess, setCopyPhotoOpSuccess] = useState(false);

  // Photo op link state
  const [showPhotoOpModal, setShowPhotoOpModal] = useState(false);
  const [photoOpLoading, setPhotoOpLoading] = useState(false);
  const [photoOpSessionsLoading, setPhotoOpSessionsLoading] = useState(false);
  const [photoOpError, setPhotoOpError] = useState('');
  const [photoOpName, setPhotoOpName] = useState('');
  const [photoOpNameEdited, setPhotoOpNameEdited] = useState(false);
  const [photoOpWindowStart, setPhotoOpWindowStart] = useState('');
  const [photoOpWindowEnd, setPhotoOpWindowEnd] = useState('');
  const [photoOpTotalPlays, setPhotoOpTotalPlays] = useState(PHOTO_OP_DEFAULT_PLAYS);
  const [photoOpBudget, setPhotoOpBudget] = useState(0);
  const [photoOpBudgetEdited, setPhotoOpBudgetEdited] = useState(false);
  const [photoOpSessionList, setPhotoOpSessionList] = useState([]);
  const [photoOpDeletingSessionId, setPhotoOpDeletingSessionId] = useState('');
  const [photoOpCreatedLinks, setPhotoOpCreatedLinks] = useState([]);

  useEffect(() => {
    if (takeover?.userUid) {
      getDoc(doc(db, 'users', takeover.userUid)).then(d => {
        if (d.exists()) setUserInfo(d.data());
      });
    }
    setStatus(takeover?.status || '');
    setEditDate(parseTakeoverDateInput(takeover?.date));
    setEditTimeSlots(getTimeSlotsFromTakeover(takeover));
    setEditSelectedHours(getSelectedHoursFromTakeover(takeover));
    setIsActivated(!!takeover?.activated);
    setActivationSuccess(false);
    setActivationError('');
  }, [takeover]);

  // Keep isActivated in sync with the live order doc
  useEffect(() => {
    if (!takeover?.id) return;
    const unsub = onSnapshot(doc(db, 'orders', takeover.id), (snap) => {
      if (snap.exists()) setIsActivated(!!snap.data()?.activated);
    }, (err) => {
      console.warn('Activation listener error:', err);
    });
    return () => unsub();
  }, [takeover?.id]);

  // Poll Pulseflow bid archive for billed plays (not the old Firestore wins subcollection)
  useEffect(() => {
    let isMounted = true;
    const campaignIds = [
      takeover?.takeoverDspCampaignId,
      dspCampaign?.id,
      dspCampaign?.dspCampaignId,
      takeover?.id,
      takeover?.takeoverDspCampaignId ? `warmup-${takeover.takeoverDspCampaignId}` : '',
    ].filter(Boolean);
    if (!campaignIds.length) return undefined;

    const fetchWins = async () => {
      if (!isMounted) return;
      if (!playsLoadedRef.current) setTotalWinsLoading(true);
      try {
        const stats = await fetchPulseflowPlayStats(campaignIds);
        let plays = stats.plays || 0;
        let spend = stats.spend || 0;
        let lastAt = stats.lastPlayAt || null;
        if (!plays) {
          const fallback = await fetchFirestoreWinsFallback(campaignIds);
          plays = fallback.plays || 0;
          lastAt = lastAt || fallback.lastPlayAt || null;
        }
        if (!isMounted) return;
        setTotalWins(plays);
        setPlaySpend(spend);
        setLastPlayAt(lastAt);
        playsLoadedRef.current = true;
      } catch (e) {
        console.warn('Failed to fetch Pulseflow plays:', e);
      } finally {
        if (isMounted) setTotalWinsLoading(false);
      }
    };

    fetchWins();
    const interval = setInterval(fetchWins, 30000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [takeover?.takeoverDspCampaignId, takeover?.id, dspCampaign?.id, dspCampaign?.dspCampaignId]);

  // Live bid doughnut — Pulseflow RTDB last 30 min (same source as the customer campaign pie)
  useEffect(() => {
    const campaignIds = new Set(getTakeoverArchiveIds(takeover, dspCampaign));
    if (!campaignIds.size) {
      return undefined;
    }

    let pending = 0;
    let liveSlice = { ...EMPTY_LIVE_BID_STATS };
    const winsSnaps = new Map();
    let publishTimer = null;
    const applyStats = (updater) => {
      setLiveBidStats((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return liveBidStatsEqual(prev, next) ? prev : next;
      });
    };
    const publish = (nextLive, nextPending) => {
      if (nextLive) liveSlice = nextLive;
      if (typeof nextPending === 'number') pending = nextPending;
      if (publishTimer) return;
      publishTimer = setTimeout(() => {
        publishTimer = null;
        const liveTotal = liveSlice.playouts + liveSlice.losses + liveSlice.noBids + liveSlice.bids + pending;
        applyStats((prev) => {
          if (liveTotal === 0 && (prev.playouts + prev.losses + prev.noBids + prev.bids + prev.pending) > 0) {
            return prev;
          }
          return {
            ...liveSlice,
            pending,
            source: liveTotal > 0 ? 'live' : prev.source,
          };
        });
      }, 400);
    };

    const recountPending = () => {
      const nowMs = Date.now();
      let count = 0;
      winsSnaps.forEach((snapshot) => {
        if (!snapshot?.exists?.()) return;
        snapshot.forEach((child) => {
          const val = child.val() || {};
          const expires = Number(val.pendingExpiresAtMs || val.pending_expires_at_ms || 0);
          if (expires > nowMs) {
            count += 1;
            return;
          }
          const baseMs = parseLiveEventTs(val.timestamp ?? val.ts);
          if (baseMs && baseMs + 2 * 60 * 1000 > nowMs) count += 1;
        });
      });
      publish(null, count);
    };

    const eventsRef = rtdbRef(rtdb, 'pulseflow/events');
    const eventsQuery = rtdbQuery(eventsRef, limitToLast(1000));
    const unsubs = [
      onValue(eventsQuery, (snapshot) => {
        const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
        const impressionIds = new Set();
        const winIds = new Set();
        const pendingBidIds = new Set();
        const lossReasons = {};
        const noBidReasons = {};
        let losses = 0;
        let noBids = 0;
        let bids = 0;

        const resolveInFlightBid = (auctionId) => {
          if (!auctionId || !pendingBidIds.has(auctionId)) return;
          pendingBidIds.delete(auctionId);
          bids = Math.max(0, bids - 1);
        };

        if (snapshot.exists()) {
          snapshot.forEach((child) => {
            const val = child.val() || {};
            const cid = String(val.campaignId || val.campaign_id || val.data?.campaignId || '');
            if (!campaignIds.has(cid)) return;
            const tsMs = parseLiveEventTs(val.ts ?? val.timestamp ?? val.createdAt ?? val.createdAtMs);
            if (!tsMs || tsMs < thirtyMinutesAgo) return;
            const type = String(val.type || val.status || '').toLowerCase();
            const auctionId = String(val.auctionId || val.auction_id || val.bidRequestId || val.bid_request_id || '').trim();
            const reasonCode = String(val.reasonCode ?? val.reason ?? val.data?.reasonCode ?? val.data?.primaryReasonCode ?? 'unknown');
            const reasonDescription = String(val.reasonDescription || val.reason || val.data?.reasonDescription || val.data?.primaryReasonDescription || reasonCode);

            if (type === 'impression') {
              if (auctionId) impressionIds.add(auctionId);
              resolveInFlightBid(auctionId);
            } else if (type === 'win' || type === 'billed' || type === 'win_pending_billing') {
              if (auctionId) winIds.add(auctionId);
              resolveInFlightBid(auctionId);
            } else if (type === 'loss') {
              resolveInFlightBid(auctionId);
              losses += 1;
              lossReasons[reasonCode] = {
                reasonCode,
                reasonDescription,
                count: (lossReasons[reasonCode]?.count || 0) + 1,
              };
            } else if (type === 'no_bid' || type === 'nobid') {
              resolveInFlightBid(auctionId);
              noBids += 1;
              noBidReasons[reasonCode] = {
                reasonCode,
                reasonDescription,
                count: (noBidReasons[reasonCode]?.count || 0) + 1,
              };
            } else if (type === 'bid') {
              if (auctionId && pendingBidIds.has(auctionId)) return;
              if (auctionId) pendingBidIds.add(auctionId);
              bids += 1;
            }
          });
        }

        publish({
          playouts: impressionIds.size || winIds.size,
          pending: 0,
          losses,
          noBids,
          bids,
          lossReasons: topReasonList(lossReasons),
          noBidReasons: topReasonList(noBidReasons),
          source: 'live',
        });
      }, (err) => console.warn('Takeover live bid listener error:', err)),
    ];

    campaignIds.forEach((campaignId) => {
      const winsQuery = rtdbQuery(rtdbRef(rtdb, 'pulseflow/active_wins'), orderByChild('campaignId'), equalTo(campaignId));
      unsubs.push(onValue(winsQuery, (snapshot) => {
        winsSnaps.set(campaignId, snapshot);
        recountPending();
      }, () => {}));
    });
    const pendingTick = setInterval(recountPending, 15000);

    let cancelled = false;
    const loadArchiveFallback = async () => {
      try {
        const startDate = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        let best = null;
        for (const campaignId of campaignIds) {
          const qs = `campaignId=${encodeURIComponent(campaignId)}&startDate=${encodeURIComponent(startDate)}`;
          const res = await fetch(`${PULSEFLOW_API}/api/archive/bid-logs/summary?${qs}`);
          if (!res.ok) continue;
          const summary = await res.json();
          const total = Number(summary.total || 0);
          if (!best || total > Number(best.total || 0)) best = summary;
          if (total > 0) break;
        }
        if (cancelled || !best) return;
        applyStats((prev) => {
          const liveTotal = prev.playouts + prev.pending + prev.losses + prev.noBids + prev.bids;
          if (liveTotal > 0 && prev.source === 'live') return prev;
          return {
            ...EMPTY_LIVE_BID_STATS,
            playouts: Number(best.wins || 0),
            losses: Number(best.losses || 0),
            noBids: Number(best.noBids || 0),
            source: 'archive',
          };
        });
      } catch (e) {
        console.warn('Takeover bid summary fallback failed:', e);
      }
    };
    loadArchiveFallback();

    return () => {
      cancelled = true;
      clearTimeout(publishTimer);
      clearInterval(pendingTick);
      unsubs.forEach((fn) => {
        if (typeof fn === 'function') fn();
      });
    };
  }, [takeover?.takeoverDspCampaignId, takeover?.id, dspCampaign?.id, dspCampaign?.dspCampaignId]);

  const isDspOrder = !!takeover?.takeoverDspCampaignId;
  // NOTE: Despite the name, this is the Firestore doc id in `takeovers/<id>` for DSP-backed takeovers
  // (example: "takeover-7ktt4mw6cVjLaGPwoxoL").
  const dspCampaignId = takeover?.takeoverDspCampaignId || '';

  // Match CreateCampaignView.vue: normalize format key for per-format IAB selection
  const normalizeFormatKey = (key) => {
    if (!key) return '2880x960';
    return String(key).replace(/\s/g, '').toLowerCase();
  };

  const getNormalizedKeyForCreative = (creative) => {
    const dimRaw = creative?.dimensions || creative?.format || '';
    const parsed = parseDimensions(dimRaw);
    if (parsed?.width && parsed?.height) return normalizeFormatKey(`${parsed.width}x${parsed.height}`);
    // Fallback: if dim string already has an x, normalize it
    if (String(dimRaw || '').toLowerCase().includes('x')) return normalizeFormatKey(dimRaw);
    return '2880x960';
  };

  const getIabName = (code) => {
    const c = String(code || '').trim();
    if (!c) return '';
    const match = (iabCategories || []).find((it) => String(it?.code || '').trim() === c);
    return String(match?.name || '').trim() || 'Unknown category';
  };

  const getFilteredIabCategories = () => {
    const term = String(iabSearchTerm || '').trim().toLowerCase();
    const list = iabCategories || [];
    const filtered = !term
      ? list
      : list.filter((c) => {
          const code = String(c?.code || '').toLowerCase();
          const name = String(c?.name || '').toLowerCase();
          return code.includes(term) || name.includes(term);
        });
    if (!term) return filtered.slice(0, 200);
    return filtered;
  };

  const openIabPicker = ({ normalizedKey }) => {
    setPendingIabNormalizedKey(normalizedKey || '');
    setPendingIabCode(String(iabCategoryByFormat?.[normalizedKey] || '').trim());
    setIabSearchTerm('');
    setShowIabPickerModal(true);
  };

  const confirmIabAndContinue = async () => {
    const code = String(pendingIabCode || '').trim();
    const normalizedKey = String(pendingIabNormalizedKey || '').trim();
    if (!code) {
      alert('Please select an IAB category');
      return;
    }
    if (!normalizedKey) {
      setShowIabPickerModal(false);
      return;
    }

    // Remember last selection per format for convenience
    setIabCategoryByFormat((prev) => ({ ...(prev || {}), [normalizedKey]: code }));

    // Close modal
    setShowIabPickerModal(false);
    setPendingIabNormalizedKey('');
    setPendingIabCode('');
    setIabSearchTerm('');

    // Continue submit if the picker was triggered by a submit attempt
    if (pendingDspSubmitCreative) {
      const c = pendingDspSubmitCreative;
      setPendingDspSubmitCreative(null);
      // Pass the selection directly to avoid racing React state updates.
      await handleSubmitDspCreative({ ...(c || {}), __forceIab: code, __forceNormalizedKey: normalizedKey });
    }
  };

  const promptExistsChoice = async ({ title, body }) => {
    setExistsChoiceTitle(title || 'Creative Already Exists');
    setExistsChoiceBody(body || '');
    setExistsChoiceLoading(false);
    setExistsChoiceOpen(true);
    return await new Promise((resolve) => {
      existsChoiceResolverRef.current = resolve;
    });
  };

  const resolveExistsChoice = (choice) => {
    const r = existsChoiceResolverRef.current;
    existsChoiceResolverRef.current = null;
    setExistsChoiceOpen(false);
    setExistsChoiceLoading(false);
    if (typeof r === 'function') r(choice);
  };

  useEffect(() => {
    let isMounted = true;
    async function loadIabCategories() {
      if (!isDspOrder) return;
      setIabCategoriesLoading(true);
      setIabCategoriesError('');
      try {
        const res = await fetch(`${PERION_API_BRIDGE_BASE}/api/iab-categories`);
        if (!res.ok) throw new Error(`IAB categories fetch failed (${res.status})`);
        const data = await res.json();
        if (!isMounted) return;
        setIabCategories(Array.isArray(data) ? data : []);
      } catch (e) {
        console.warn('Failed to load IAB categories:', e?.message || e);
        if (!isMounted) return;
        setIabCategoriesError('Failed to load IAB categories');
        setIabCategories([]);
      } finally {
        if (isMounted) setIabCategoriesLoading(false);
      }
    }
    loadIabCategories();
    return () => { isMounted = false; };
  }, [isDspOrder]);

  useEffect(() => {
    if (!showIabPickerModal) return;
    const t = setTimeout(() => iabSearchInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [showIabPickerModal]);

  const sendStatusPushNotification = async ({ newStatus }) => {
    const userId = takeover?.userUid;
    if (!userId) return;

    // Only send for the statuses requested
    const notifyStatuses = new Set(['Rejected', 'Pending', 'Approved', 'Upcoming', 'Canceled']);
    if (!notifyStatuses.has(newStatus)) return;

    const signName = takeover?.signName || 'your takeover';
    const title = 'Order Update';
    const body = `Your order for ${signName} is now ${newStatus}.`;

    const payload = {
      userId,
      title,
      body,
      data: {
        messageId: `order_${takeover?.id || ''}_${newStatus}_${Date.now()}`,
        type: 'order_status_update',
        orderId: takeover?.id || '',
        status: newStatus
      }
      // imageUrl: optional
    };

    try {
      const res = await fetch(`${PROXY_BASE_URL}/api/send-push-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        console.warn('Push notification failed:', res.status, txt);
      }
    } catch (e) {
      console.warn('Push notification error:', e);
    }
  };

  useEffect(() => {
    let isMounted = true;
    async function loadDspCampaign() {
      if (!isDspOrder || !dspCampaignId) {
        setDspCampaign(null);
        return;
      }
      setDspCampaignLoading(true);
      try {
        const snap = await getDoc(doc(db, 'takeovers', dspCampaignId));
        if (!isMounted) return;
        if (snap.exists()) setDspCampaign({ id: snap.id, ...snap.data() });
        else setDspCampaign(null);
      } catch (e) {
        console.error('Failed to load DSP takeover campaign:', e);
        if (isMounted) setDspCampaign(null);
      } finally {
        if (isMounted) setDspCampaignLoading(false);
      }
    }
    loadDspCampaign();
    return () => { isMounted = false; };
  }, [isDspOrder, dspCampaignId]);

  useEffect(() => {
    if (!dspCampaign) {
      setDspAdvertiserUrlDraft('');
      setDspBudgetDraft('');
      return;
    }
    const existingBudget = dspCampaign.totalBudget ?? dspCampaign.budget;
    setDspBudgetDraft(existingBudget != null && existingBudget !== '' ? String(existingBudget) : '');
    const raw =
      dspCampaign.clientWebsite ||
      dspCampaign.clientwebsite ||
      dspCampaign.clientDomain ||
      dspCampaign.client_domain ||
      dspCampaign.advertiserDomain ||
      dspCampaign.advertiser_domain ||
      dspCampaign.domain ||
      dspCampaign.website ||
      '';
    setDspAdvertiserUrlDraft(String(raw || '').trim());
  }, [dspCampaign]);

  useEffect(() => {
    let isMounted = true;
    async function loadDspCreativeMeta() {
      const creativesArr = Array.isArray(dspCampaign?.creatives) ? dspCampaign.creatives : [];
      const assigned = dspCampaign?.assignedCreatives && typeof dspCampaign.assignedCreatives === 'object'
        ? dspCampaign.assignedCreatives
        : null;
      const assignedArr = assigned
        ? Object.values(assigned).flatMap(v => Array.isArray(v) ? v : [])
        : [];
      const creativeList = creativesArr.length > 0 ? creativesArr : assignedArr;

      if (!isDspOrder || creativeList.length === 0) {
        if (isMounted) setDspCreativeMetaById({});
        return;
      }
      try {
        // Prefer seller-scoped DSP ids (matches CreateCampaignView + CreativeLibraryView behavior).
        // This prevents accidental status checks against a stale/incorrect single `dspCreativeId`.
        const sellerIdForLookup = pickPublisherIdFromCampaign(dspCampaign);
        const meta = {};
        for (const c of creativeList) {
          const creativeDocId = c?.id || c?.creative_id || c?.creativeId;
          if (!creativeDocId) continue;
          const snap = await getDoc(doc(db, 'creatives', creativeDocId));
          if (snap.exists()) {
            const data = snap.data();
            const map = (data?.dspCreativeIdMap && typeof data.dspCreativeIdMap === 'object') ? data.dspCreativeIdMap : null;
            const mapped =
              (sellerIdForLookup && map && (map[sellerIdForLookup] || map[safeFirestoreKey(sellerIdForLookup)])) ||
              '';
            const fromArray = Array.isArray(data?.dspCreativeIds) && data.dspCreativeIds.length > 0
              ? String(data.dspCreativeIds[0] || '').trim()
              : '';
            const fallbackSingle = String(
              data.dspCreativeId || data.dspCreativeID || data.dspId || ''
            ).trim();
            meta[creativeDocId] = {
              dspCreativeId: String(mapped || fromArray || fallbackSingle || '').trim(),
              dspCampaignId: String(data.dspCampaignId || data.takeoverDspCampaignId || '').trim(),
              dspSubmittedAt: data.dspSubmittedAt || null,
              moderationStatus: data.moderationStatus || '',
              statuses: data.statuses || {}
            };
          } else {
            meta[creativeDocId] = meta[creativeDocId] || {};
          }
        }
        if (isMounted) setDspCreativeMetaById(meta);
      } catch (e) {
        console.error('Failed to load DSP creative metadata:', e);
      }
    }
    loadDspCreativeMeta();
    return () => { isMounted = false; };
  }, [isDspOrder, dspCampaign?.id]);

  // Auto-fetch live publisher approval status for every creative that has a DSP id
  useEffect(() => {
    let isMounted = true;
    const dspIds = [...new Set(
      Object.values(dspCreativeMetaById)
        .map(m => String(m?.dspCreativeId || '').trim())
        .filter(Boolean)
    )];
    if (dspIds.length === 0) return;

    async function fetchAllStatuses() {
      for (const dspId of dspIds) {
        if (!isMounted) break;
        try {
          const statusObj = await fetchDspCreativeStatus(dspId);
          if (isMounted) setDspCreativeStatusByDspId(prev => ({ ...prev, [dspId]: statusObj }));
        } catch (e) {
          console.warn(`Auto status fetch failed for DSP creative ${dspId}:`, e);
        }
      }
    }
    fetchAllStatuses();
    return () => { isMounted = false; };
  }, [dspCreativeMetaById]);

  const extractDomain = (website) => {
    if (!website) return '';
    try {
      const u = new URL(website.startsWith('http') ? website : `https://${website}`);
      return (u.hostname || '').replace(/^www\./, '');
    } catch {
      return String(website).replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    }
  };

  // Match CreateCampaignView.vue seller id validation (HS-XX-<int>) and "find first usable publisher id"
  const isValidHivestackSellerId = (v) => /^HS-[A-Z]{2}-[1-9]\d*$/.test(String(v || '').trim());

  const pickPublisherIdFromCampaign = (campaign) => {
    const signs = Array.isArray(campaign?.signs) ? campaign.signs : [];
    const candidates = [
      // Prefer signs[] in order
      ...signs.map((s) => getSellerIdFromSign(s)).filter(Boolean),
      // Fallback legacy top-level fields if present
      campaign?.seller_id,
      campaign?.sellerId,
      campaign?.publisher_id,
      campaign?.publisherId,
      campaign?.publisher
    ].filter(Boolean);
    const trimmed = candidates.map((c) => String(c).trim()).filter(Boolean);
    // Prefer an HS-.. formatted id if possible
    const valid = trimmed.find(isValidHivestackSellerId);
    return valid || trimmed[0] || '';
  };

  // Match CreateCampaignView.vue client domain behavior: derive domain from multiple fields.
  const getAdvertiserDomainFromCampaign = (campaign) => {
    const c = campaign || {};
    const maybe =
      c?.clientWebsite ||
      c?.clientwebsite ||
      c?.clientDomain ||
      c?.client_domain ||
      c?.advertiserDomain ||
      c?.advertiser_domain ||
      c?.domain ||
      c?.website ||
      '';
    const d = extractDomain(maybe);
    return d || '';
  };

  const saveDspAdvertiserUrl = async () => {
    if (!isDspOrder || !dspCampaignId) return;
    const trimmed = String(dspAdvertiserUrlDraft || '').trim();
    if (!trimmed) {
      alert('Enter an advertiser URL or domain.');
      return;
    }
    const domain = extractDomain(trimmed);
    if (!domain) {
      alert('Could not parse a valid domain from that value.');
      return;
    }
    setDspAdvertiserUrlSaving(true);
    try {
      const patch = {
        clientWebsite: trimmed,
        advertiserDomain: domain,
        advertiser_domain: domain,
        updatedAt: new Date()
      };
      await setDoc(doc(db, 'takeovers', dspCampaignId), patch, { merge: true });
      await setDoc(doc(db, 'active_dsp_campaigns', dspCampaignId), patch, { merge: true });

      const warmupId = `warmup-${dspCampaignId}`;
      try {
        const warmSnap = await getDoc(doc(db, 'takeovers', warmupId));
        if (warmSnap.exists()) {
          await setDoc(doc(db, 'takeovers', warmupId), patch, { merge: true });
          await setDoc(doc(db, 'active_dsp_campaigns', warmupId), patch, { merge: true });
        }
      } catch (e) {
        console.warn('Warm-up advertiser URL mirror failed (continuing):', e);
      }

      setDspCampaign((prev) => (prev ? { ...prev, ...patch } : prev));
      alert('Advertiser URL saved to takeover and active DSP campaign.');
    } catch (e) {
      alert(e?.message || 'Failed to save advertiser URL');
    } finally {
      setDspAdvertiserUrlSaving(false);
    }
  };

  const saveDspBudget = async () => {
    if (!isDspOrder || !dspCampaignId) return;
    const parsed = Number(dspBudgetDraft);
    if (!isFinite(parsed) || parsed < 0) {
      alert('Enter a valid DSP budget (0 or greater).');
      return;
    }
    setDspBudgetSaving(true);
    try {
      const patch = {
        budget: parsed,
        totalBudget: parsed,
        updatedAt: new Date()
      };
      await setDoc(doc(db, 'takeovers', dspCampaignId), patch, { merge: true });
      await setDoc(doc(db, 'active_dsp_campaigns', dspCampaignId), patch, { merge: true });
      setDspCampaign((prev) => (prev ? { ...prev, ...patch } : prev));
      alert('DSP budget saved to takeover and active DSP campaign.');
    } catch (e) {
      alert(e?.message || 'Failed to save DSP budget');
    } finally {
      setDspBudgetSaving(false);
    }
  };

  // Match CreateCampaignView.vue: determine "already submitted for THIS publisher" via dspCreativeIdMap when available.
  const getCreativeDspIdForSeller = (creativeDoc, sellerId) => {
    const pub = String(sellerId || '').trim();
    if (!pub) return '';
    const d = creativeDoc || {};
    const map = (d?.dspCreativeIdMap && typeof d.dspCreativeIdMap === 'object') ? d.dspCreativeIdMap : null;
    if (map) {
      const direct = map[pub];
      if (direct) return String(direct).trim();
      const safe = map[safeFirestoreKey(pub)];
      if (safe) return String(safe).trim();
    }
    // Fallback: some docs store a single dspCreativeId (not seller-scoped)
    const single =
      d?.dspCreativeId ||
      d?.dspCreativeID ||
      d?.dspId ||
      '';
    return String(single || '').trim();
  };

  const parseDimensions = (dim) => {
    if (!dim) return { width: undefined, height: undefined };
    const m = String(dim).toLowerCase().match(/(\d+)\s*x\s*(\d+)/);
    if (!m) return { width: undefined, height: undefined };
    return { width: Number(m[1]), height: Number(m[2]) };
  };

  const guessMimeType = (url, type) => {
    const lower = String(url || '').toLowerCase();
    if (type === 'video') {
      if (lower.includes('.webm')) return 'video/webm';
      if (lower.includes('.mov')) return 'video/quicktime';
      return 'video/mp4';
    }
    if (lower.includes('.png')) return 'image/png';
    if (lower.includes('.webp')) return 'image/webp';
    return 'image/jpeg';
  };

  const safeFirestoreKey = (k) => String(k || '').replace(/[./#[\]$]/g, '_');

  const getSellerIdFromSign = (sign) => {
    if (!sign || typeof sign !== 'object') return '';
    return (
      sign.seller_id ||
      sign.sellerId ||
      sign.publisher_id ||
      sign.publisherId ||
      sign.publisher ||
      sign.SellerID ||
      sign.SellerId ||
      sign['Seller ID'] ||
      sign['Seller Id'] ||
      ''
    );
  };

  const getAdunitIdFromSign = (sign) => {
    if (!sign || typeof sign !== 'object') return '';
    return sign.adunit_id || sign.adunitId || sign.adunitIds?.[0] || sign.id || '';
  };

  const getOpenRtbUuidFromSign = (sign) => {
    if (!sign || typeof sign !== 'object') return '';
    return (
      sign.openrtb_uuid ||
      sign.openRtbUuid ||
      sign.openRTBUUID ||
      sign.openRtbUuids?.[0] ||
      sign['OpenRTB UUID'] ||
      ''
    );
  };

  const getOxFloorCpm = (sign) => {
    if (!sign || typeof sign !== 'object') return 0;
    const raw =
      sign.ox_floor_cpm ??
      sign.oxFloorCpm ??
      sign['OX Floor CPM'] ??
      sign['OX Floor Cpm'] ??
      sign.ox_floor ??
      0;
    const n = Number(raw);
    return isFinite(n) ? n : 0;
  };

  const buildBidParamsFromSign0 = (campaign) => {
    const sign0 = campaign?.signs?.[0] || {};
    const floorCpm = getOxFloorCpm(sign0);
    const maxCPM = floorCpm ? floorCpm * 2 : 0;
    const baseCpmCents = floorCpm ? Math.round(floorCpm * 100 * 2) : 0;
    return {
      bidStrategy: 'cpm_target',
      ...(baseCpmCents ? { baseCpmCents } : {}),
      ...(maxCPM ? { maxCPM } : {})
    };
  };

  const deriveScheduleFromWeeklyTimeSegments = (weeklyTimeSegments) => {
    const segments = Array.isArray(weeklyTimeSegments) ? weeklyTimeSegments : [];
    const activeDaysSet = new Set();
    const activeHoursSet = new Set();

    const parseHour = (t) => {
      const m = String(t || '').match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return null;
      const h = Number(m[1]);
      if (!isFinite(h) || h < 0 || h > 23) return null;
      return h;
    };

    for (const seg of segments) {
      const day = Number(seg?.day);
      if (isFinite(day) && day >= 0 && day <= 6) activeDaysSet.add(day);
      const sh = parseHour(seg?.startTime);
      const eh = parseHour(seg?.endTime);
      if (sh == null || eh == null) continue;
      const start = Math.min(sh, eh);
      const end = Math.max(sh, eh);
      for (let h = start; h <= end; h++) activeHoursSet.add(h);
    }

    return {
      activeDays: [...activeDaysSet].sort((a, b) => a - b),
      activeHours: [...activeHoursSet].sort((a, b) => a - b)
    };
  };

  const toJsDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v?.toDate === 'function') return v.toDate(); // Firestore Timestamp
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  const toDateTimeLocalString = (v) => {
    const d = toJsDate(v);
    if (!d) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openDspScheduleEditor = async () => {
    if (!isDspOrder || !dspCampaignId) return;
    setDspScheduleError('');
    setDspScheduleLoading(true);
    try {
      // Load freshest campaign doc for schedule fields
      let campaign = dspCampaign;
      try {
        const snap = await getDoc(doc(db, 'takeovers', dspCampaignId));
        if (snap.exists()) campaign = { id: snap.id, ...snap.data() };
      } catch (_) {}

      const startDate = toJsDate(campaign?.startDate) || new Date();
      const endDate = toJsDate(campaign?.endDate) || dayjs(startDate).add(1, 'day').toDate();

      // Prefer explicit weeklyTimeSegments; otherwise fall back to full-day segments for activeDays.
      const weeklyTimeSegments =
        (Array.isArray(campaign?.weeklyTimeSegments) && campaign.weeklyTimeSegments) ||
        (Array.isArray(campaign?.weekly_time_segments) && campaign.weekly_time_segments) ||
        [];
      const activeDays =
        (Array.isArray(campaign?.activeDays) && campaign.activeDays) ||
        (Array.isArray(campaign?.active_days) && campaign.active_days) ||
        deriveScheduleFromWeeklyTimeSegments(weeklyTimeSegments).activeDays ||
        [0, 1, 2, 3, 4, 5, 6];

      // Take first segment as a default time window (matches the “single window” editor UI)
      const seg0 = weeklyTimeSegments?.[0] || null;
      const startTime = typeof seg0?.startTime === 'string' ? seg0.startTime : '00:00';
      const endTime = typeof seg0?.endTime === 'string' ? seg0.endTime : '23:59';

      setDspEditStartDateTime(toDateTimeLocalString(startDate));
      setDspEditEndDateTime(toDateTimeLocalString(endDate));
      setDspEditDays([...new Set((activeDays || []).map((d) => Number(d)).filter((d) => isFinite(d) && d >= 0 && d <= 6))]);
      setDspEditStartTime(startTime || '00:00');
      setDspEditEndTime(endTime || '23:59');
      setDspApplyToWarmup(false);
      setDspScheduleOpen(true);
    } catch (e) {
      setDspScheduleError(e?.message || 'Failed to load schedule');
    } finally {
      setDspScheduleLoading(false);
    }
  };

  const saveDspSchedule = async () => {
    if (!isDspOrder || !dspCampaignId) return;
    setDspScheduleError('');
    const startDate = new Date(dspEditStartDateTime);
    const endDate = new Date(dspEditEndDateTime);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      setDspScheduleError('Start and end date/time are required.');
      return;
    }
    if (endDate <= startDate) {
      setDspScheduleError('End date/time must be after start date/time.');
      return;
    }
    const days = [...new Set((dspEditDays || []).map((d) => Number(d)).filter((d) => isFinite(d) && d >= 0 && d <= 6))].sort((a, b) => a - b);
    if (days.length === 0) {
      setDspScheduleError('Select at least one active day.');
      return;
    }
    const startTime = String(dspEditStartTime || '').trim();
    const endTime = String(dspEditEndTime || '').trim();
    if (!/^\d{2}:\d{2}$/.test(startTime) || !/^\d{2}:\d{2}$/.test(endTime)) {
      setDspScheduleError('Start and end time must be in HH:MM format.');
      return;
    }

    const weeklyTimeSegments = days.map((day) => ({ day, startTime, endTime }));
    const derived = deriveScheduleFromWeeklyTimeSegments(weeklyTimeSegments);
    const patch = {
      startDate,
      endDate,
      weeklyTimeSegments,
      activeDays: derived.activeDays,
      activeHours: derived.activeHours,
      updatedAt: new Date()
    };

    setDspScheduleLoading(true);
    try {
      await setDoc(doc(db, 'takeovers', dspCampaignId), patch, { merge: true });
      await setDoc(doc(db, 'active_dsp_campaigns', dspCampaignId), patch, { merge: true });

      if (dspApplyToWarmup) {
        const warmupId = `warmup-${dspCampaignId}`;
        const warmSnap = await getDoc(doc(db, 'takeovers', warmupId));
        if (warmSnap.exists()) {
          await setDoc(doc(db, 'takeovers', warmupId), patch, { merge: true });
          await setDoc(doc(db, 'active_dsp_campaigns', warmupId), patch, { merge: true });
        }
      }

      // Keep local state in sync
      setDspCampaign((prev) => prev ? ({ ...prev, ...patch }) : prev);
      setDspScheduleOpen(false);
      if (refreshTakeover) refreshTakeover();
    } catch (e) {
      setDspScheduleError(e?.message || 'Failed to save schedule');
    } finally {
      setDspScheduleLoading(false);
    }
  };

  const buildScheduleFix = (campaign) => {
    const weeklyTimeSegments =
      (Array.isArray(campaign?.weeklyTimeSegments) && campaign.weeklyTimeSegments) ||
      (Array.isArray(campaign?.weekly_time_segments) && campaign.weekly_time_segments) ||
      (Array.isArray(campaign?.weeklySegments) && campaign.weeklySegments) ||
      [];

    const activeDays =
      (Array.isArray(campaign?.activeDays) && campaign.activeDays) ||
      (Array.isArray(campaign?.active_days) && campaign.active_days) ||
      [];

    const activeHours =
      (Array.isArray(campaign?.activeHours) && campaign.activeHours) ||
      (Array.isArray(campaign?.active_hours) && campaign.active_hours) ||
      (Array.isArray(campaign?.activehours) && campaign.activehours) ||
      [];

    const derived = deriveScheduleFromWeeklyTimeSegments(weeklyTimeSegments);
    return {
      weeklyTimeSegments,
      activeDays: activeDays.length > 0 ? activeDays : derived.activeDays,
      activeHours: activeHours.length > 0 ? activeHours : derived.activeHours
    };
  };

  const buildWarmupWeeklyTimeSegments = () => {
    // Every day of week from 00:00 - 23:59
    return Array.from({ length: 7 }).map((_, day) => ({
      day,
      startTime: '00:00',
      endTime: '23:59'
    }));
  };

  const computeWarmupBudget = (totalBudget) => {
    const b = Number(totalBudget || 0);
    if (!isFinite(b) || b <= 0) return 0;
    const warm = (b / 15) * 2;
    // NOTE: Some signs can produce extremely small warm-up budgets (e.g. <$0.20),
    // which can break bidder logic (interpreted as "no budget remaining").
    // Enforce a minimum warm-up budget of $1.50.
    const minWarmupBudget = 1.5;
    const rounded = Math.round(warm * 100) / 100;
    return Math.max(minWarmupBudget, rounded);
  };

  const handleCreateWarmupCampaign = async () => {
    if (!isDspOrder) return;
    if (!dspCampaignId || !dspCampaign) {
      alert('DSP takeover doc not loaded yet.');
      return;
    }

    const ok = window.confirm(
      'Create a warm-up campaign for player approval?\n\nThis will create a new DSP takeover doc and mirror it into active_dsp_campaigns so it can start bidding.'
    );
    if (!ok) return;

    setWarmupLoading(true);
    try {
      // Re-load campaign fresh so we copy any recently persisted fields (creativeDetails, IAB mappings, etc.)
      let baseCampaign = dspCampaign;
      try {
        const fresh = await getDoc(doc(db, 'takeovers', dspCampaignId));
        if (fresh.exists()) baseCampaign = { id: fresh.id, ...fresh.data() };
      } catch (e) {
        console.warn('Warm-up: could not refresh base campaign (using local state):', e);
      }

      const draftUrl = String(dspAdvertiserUrlDraft || '').trim();
      if (draftUrl) {
        const d = extractDomain(draftUrl);
        baseCampaign = {
          ...baseCampaign,
          clientWebsite: draftUrl,
          ...(d ? { advertiserDomain: d, advertiser_domain: d } : {})
        };
      }

      const originalTotalBudget = baseCampaign?.totalBudget ?? baseCampaign?.budget ?? 0;
      const warmBudget = computeWarmupBudget(originalTotalBudget);
      if (!warmBudget || warmBudget <= 0) {
        alert('Could not compute warm-up budget (missing/invalid totalBudget).');
        return;
      }

      const signs = Array.isArray(baseCampaign?.signs) ? baseCampaign.signs : [];
      if (signs.length === 0) {
        alert('No signs found on this DSP takeover doc.');
        return;
      }

      // Turn targeting ON for the sign(s) so it actually enters the bidstream maps
      const targetedSigns = signs.map((s, idx) => ({ ...s, isTargeted: idx === 0 ? true : !!s.isTargeted }));
      const targetedOnly = targetedSigns.filter(s => !!s.isTargeted);

      const adunitIds = [...new Set(targetedOnly.map(getAdunitIdFromSign).filter(Boolean))];
      const openRtbUuids = [...new Set(targetedOnly.map(getOpenRtbUuidFromSign).filter(Boolean))];

      const startDate = new Date(); // now
      const endDate = dayjs(startDate).add(3, 'day').toDate(); // +3 days
      const weeklyTimeSegments = buildWarmupWeeklyTimeSegments();
      const activeDays = [0, 1, 2, 3, 4, 5, 6];
      const activeHours = Array.from({ length: 24 }).map((_, h) => h); // 0..23

      const warmupDoc = {
        ...baseCampaign,
        // identity / linkage
        isWarmup: true,
        // legacy/ops-friendly alias (some tooling expects lowercase)
        iswarmup: true,
        warmupForTakeoverId: dspCampaignId,
        warmupForOrderId: takeover?.id || null,
        name: `warmup-${dspCampaignId}`,
        ...buildBidParamsFromSign0(baseCampaign),
        // schedule
        startDate,
        endDate,
        weeklyTimeSegments,
        activeDays,
        activeHours,
        // targeting identifiers (built from targeted signs)
        signs: targetedSigns,
        adunitIds,
        openRtbUuids,
        // budget
        totalBudget: warmBudget,
        budget: warmBudget,
        // activation
        isActivated: true,
        status: 'active',
        updatedAt: new Date(),
        createdAt: new Date(),
        description: (baseCampaign?.description ? `${baseCampaign.description} ` : '') + 'Warm-up campaign for player approval.'
      };

      // Use a deterministic warm-up id so active_dsp_campaigns doc id is "warmup-<originalTakeoverId>"
      const warmupId = `warmup-${dspCampaignId}`;

      // Create warm-up takeover doc + mirror into active_dsp_campaigns (same deterministic id)
      await setDoc(doc(db, 'takeovers', warmupId), { ...warmupDoc, id: warmupId }, { merge: true });
      await setDoc(doc(db, 'active_dsp_campaigns', warmupId), { ...warmupDoc, id: warmupId }, { merge: true });

      // Link the warm-up back to the original DSP takeover doc (so we don't "lose" the relationship)
      await updateDoc(doc(db, 'takeovers', dspCampaignId), {
        warmupTakeoverId: warmupId,
        warmupCreatedAt: new Date(),
        updatedAt: new Date()
      });

      // Auto-match deals for the warm-up campaign too (writes to both takeovers/<warmupId> and active_dsp_campaigns/<warmupId>)
      try {
        await autoMatchDealIdsForCampaign({ campaign: warmupDoc, campaignId: warmupId });
      } catch (e) {
        console.warn('Warm-up deal auto-match failed:', e);
      }

      alert(`Warm-up campaign created: ${warmupId}`);
    } catch (e) {
      console.error('Warm-up campaign creation failed:', e);
      alert(`Warm-up creation failed: ${e.message || 'Unknown error'}`);
    } finally {
      setWarmupLoading(false);
    }
  };

  const fetchDealsForAutoMatch = async () => {
    const res = await fetch(`${PERION_API_BRIDGE_BASE}/api/deals?$top=1000`);
    if (!res.ok) throw new Error(`Deals fetch failed (${res.status})`);
    const data = await res.json();
    const deals = Array.isArray(data) ? data : (data?.data || []);
    return deals.map((d) => {
      const dealId = d?.dealId || d?.deal_id || d?.id || '';
      const seller_id = d?.seller_id || d?.sellerId || '';
      const name = d?.name || dealId || null;
      const floorRaw = d?.floorCpm ?? d?.floor_cpm ?? d?.dealFloorCpm ?? d?.deal_floor_cpm ?? null;
      const floorCpm = floorRaw != null && isFinite(Number(floorRaw)) ? Number(floorRaw) : null;
      const publisher = d?.publisher || d?.publisher_id || null;
      return {
        ...d,
        dealId,
        deal_id: d?.deal_id || dealId,
        seller_id,
        name,
        floorCpm,
        publisher
      };
    }).filter(d => d.dealId);
  };

  const autoMatchDealIdsForCampaign = async ({ campaign, campaignId }) => {
    const signs = Array.isArray(campaign?.signs) ? campaign.signs : [];
    const sellerIds = [...new Set(signs.map(getSellerIdFromSign).filter(Boolean))];
    if (sellerIds.length === 0) return { dealIds: Array.isArray(campaign?.dealIds) ? campaign.dealIds : [], dealId: campaign?.dealId || null };

    const deals = await fetchDealsForAutoMatch();
    const matchingDeals = deals.filter(d => d.seller_id && sellerIds.includes(d.seller_id));
    const matchingDealIds = matchingDeals.map(d => d.dealId);

    const existing = Array.isArray(campaign?.dealIds) ? campaign.dealIds : [];
    const merged = [...new Set([...existing, ...matchingDealIds])];
    const primary = merged[0] || null;

    // Merge full deal objects too (so Firestore has `deals[]`, `dealName`, `dealFloorCpm`, `publisherId` like CreateCampaignView)
    const existingDeals = Array.isArray(campaign?.deals) ? campaign.deals : [];
    const dealById = new Map();
    for (const d of existingDeals) {
      const id = d?.dealId || d?.deal_id || d?.id;
      if (id) dealById.set(id, d);
    }
    for (const d of matchingDeals) {
      const id = d?.dealId || d?.deal_id || d?.id;
      if (id) dealById.set(id, d);
    }
    const mergedDeals = merged.map(id => dealById.get(id)).filter(Boolean);
    const primaryDeal = primary ? (dealById.get(primary) || null) : null;
    const dealName = primaryDeal?.name || primaryDeal?.dealId || primary || null;
    const dealFloorCpm = primaryDeal?.floorCpm != null ? Number(primaryDeal.floorCpm) : (primaryDeal?.floor_cpm != null ? Number(primaryDeal.floor_cpm) : 0);
    const publisherId = primaryDeal?.publisher || primaryDeal?.publisher_id || null;

    const dealFields = {
      dealIds: merged,
      dealId: primary,
      deals: mergedDeals,
      dealName,
      dealFloorCpm: isFinite(dealFloorCpm) ? dealFloorCpm : 0,
      publisherId,
      updatedAt: new Date()
    };

    // Persist onto takeover doc + active mirror (merge)
    await updateDoc(doc(db, 'takeovers', campaignId), dealFields);
    await setDoc(doc(db, 'active_dsp_campaigns', campaignId), dealFields, { merge: true });

    // Keep local state in sync
    setDspCampaign(prev => prev ? ({ ...prev, ...dealFields }) : prev);

    return { dealIds: merged, dealId: primary };
  };

  const computeDspCreativeApproval = (items) => {
    const approvals = (items || []).map((it) => {
      const state =
        it?.approval_state ??
        it?.publisher_approval_state ??
        it?.media_files?.[0]?.approval_state ??
        1;
      return { publisher_id: it?.publisher_id || it?.seller_id || '', approval_state: Number(state) || 1 };
    });
    const states = approvals.map(a => a.approval_state);
    let global = 1;
    if (states.some(s => s === 3)) global = 3;
    else if (states.length > 0 && states.every(s => s === 2)) global = 2;
    else if (states.some(s => s === 2)) global = 4;
    else global = 1;
    const label = global === 2 ? 'approved' : global === 3 ? 'rejected' : global === 4 ? 'partial' : 'pending';
    return { global_approval_state: global, label, approvals };
  };

  const fetchDspCreativeStatus = async (dspCreativeId) => {
    const res = await fetch(`${PERION_API_BRIDGE_BASE}/api/creatives/${encodeURIComponent(dspCreativeId)}`);
    if (!res.ok) throw new Error(`Status fetch failed (${res.status})`);
    const data = await res.json();
    const items = Array.isArray(data) ? data : (data?.data || []);
    return computeDspCreativeApproval(items);
  };

  const persistDspCreativeStatusToFirestore = async ({ creativeDocId, dspCreativeId, sellerId, statusObj }) => {
    if (!creativeDocId) return;
    const moderationStatus = statusObj?.label || 'pending';
    const updates = {
      moderationStatus,
      dspApprovalState: statusObj?.global_approval_state || 1,
      lastApprovalCheck: new Date(),
      updatedAt: new Date()
    };
    if (sellerId) {
      updates[`statuses.${safeFirestoreKey(sellerId)}`] = moderationStatus;
    }
    if (dspCreativeId) updates.dspCreativeId = dspCreativeId;
    await updateDoc(doc(db, 'creatives', creativeDocId), updates);
  };

  const handleUpdateStatus = async (newVal) => {
    // If marking as complete, check if payment needs to be captured first
    if (newVal === 'Complete' && takeover.paymentStatus === 'authorized' && takeover.paymentType === 'authorize_only') {
      setShowPaymentModal(true);
      return;
    }

    setLoading(true);
    try {
      const ref = doc(db, 'orders', takeover.id);
      await updateDoc(ref, { status: newVal });
      setStatus(newVal);
      if (refreshTakeover) refreshTakeover();
      // Fire-and-forget push notification (don't block UI)
      sendStatusPushNotification({ newStatus: newVal });
    } catch (e) {
      alert('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const fetchSigns = async () => {
    if (allSigns.length > 0) return;
    setSignSearchLoading(true);
    try {
      const response = await fetch(`${PROXY_BASE_URL}/api/signs`);
      const data = await response.json();
      if (data.success) {
        setAllSigns(data.signs);
      }
    } catch (err) {
      console.error('Error fetching signs:', err);
    } finally {
      setSignSearchLoading(false);
    }
  };

  const handleUpdateSign = async (newSign) => {
    if (!window.confirm(`Change sign to ${newSign.asset_name || newSign.name}?`)) return;
    
    setLoading(true);
    try {
      const ref = doc(db, 'orders', takeover.id);
      const updateData = {
        signName: newSign.asset_name || newSign.name || '',
        adunit_id: newSign.adunit_id || newSign.id || '',
        signId: newSign.adunit_id || newSign.id || '', // keep both for compatibility
        address: newSign.address || '',
        signResolution: newSign.signResolution || newSign.resolution || '',
        dma: newSign.dma || '',
        signImgURL: newSign.asset_image_url || newSign.img || ''
      };
      
      await updateDoc(ref, updateData);
      setIsEditingSign(false);
      if (refreshTakeover) refreshTakeover();
      alert('Sign updated successfully');
    } catch (e) {
      console.error('Error updating sign:', e);
      alert('Failed to update sign');
    } finally {
      setLoading(false);
    }
  };

  const handleCapturePayment = async () => {
    setPaymentLoading(true);
    setPaymentError('');
    try {
      const response = await fetch('https://vue-payment-server.fly.dev/api/capture-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          transactionId: takeover.paymentTransactionId, 
          amount: takeover.paymentAmount || takeover.totalPrice 
        })
      });
      
      const data = await response.json();
      if (data.success) {
        const ref = doc(db, 'orders', takeover.id);
        await updateDoc(ref, { 
          paymentStatus: 'captured',
          captureTransactionId: data.transactionId || null,
          captureTimestamp: new Date()
          // status: 'Complete' // removed per request
        });
        
        setShowPaymentModal(false);
        // setStatus('Complete'); // removed per request
        if (refreshTakeover) refreshTakeover();
        alert('Payment captured successfully!');
      } else {
        setPaymentError(data.message || 'Payment capture failed');
      }
    } catch (err) {
      setPaymentError('Network error. Could not reach payment server.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleVoidTransaction = async () => {
    if (!window.confirm('Are you sure you want to void this authorization? This cannot be undone.')) return;
    setPaymentLoading(true);
    setPaymentError('');
    try {
      const response = await fetch('https://vue-payment-server.fly.dev/api/void-transaction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          transactionId: takeover.paymentTransactionId,
          campaignId: takeover.id // Reusing same logic as campaigns
        })
      });
      const data = await response.json();
      if (data.success) {
        const ref = doc(db, 'orders', takeover.id);
        await updateDoc(ref, { 
          paymentStatus: 'voided',
          status: 'Rejected'
        });
        alert('Transaction voided successfully.');
        if (refreshTakeover) refreshTakeover();
        // Status was set to Rejected as part of voiding; notify the user
        sendStatusPushNotification({ newStatus: 'Rejected' });
      } else {
        alert(data.message || 'Void failed');
      }
    } catch (err) {
      alert('Network error connecting to payment server.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleRefundPayment = async () => {
    const amount = prompt(`Enter refund amount (max ${formatCurrency(takeover.paymentAmount || takeover.totalPrice)}):`, takeover.paymentAmount || takeover.totalPrice);
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0 || parseFloat(amount) > (takeover.paymentAmount || takeover.totalPrice)) {
      alert('Invalid refund amount.');
      return;
    }

    setPaymentLoading(true);
    try {
      const response = await fetch('https://vue-payment-server.fly.dev/api/refund-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          transactionId: takeover.paymentTransactionId || takeover.captureTransactionId,
          amount: parseFloat(amount),
          campaignId: takeover.id
        })
      });
      const data = await response.json();
      if (data.success) {
        const ref = doc(db, 'orders', takeover.id);
        await updateDoc(ref, { 
          paymentStatus: 'refunded',
          refundAmount: parseFloat(amount),
          refundTimestamp: new Date()
        });
        alert(`Successfully refunded ${formatCurrency(amount)}.`);
        if (refreshTakeover) refreshTakeover();
      } else {
        alert(data.message || 'Refund failed');
      }
    } catch (err) {
      alert('Network error connecting to payment server.');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Returns true if current time is within the activation window:
  // 5 minutes before → 1 hour after the first scheduled time slot
  const isInActivationWindow = () => {
    const dateStr = takeover?.date;
    const slots = takeover?.timeSlots || [];
    if (!dateStr || slots.length === 0) return false;
    try {
      const base = dayjs(dateStr).format('YYYY-MM-DD');
      const slot = slots[0]; // e.g., "10:00 AM" or "10:00"
      const formats = ['YYYY-MM-DD h:mm A', 'YYYY-MM-DD hh:mm A', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD H:mm'];
      let slotDate = null;
      for (const fmt of formats) {
        const attempt = dayjs(`${base} ${slot}`, fmt);
        if (attempt.isValid()) { slotDate = attempt; break; }
      }
      if (!slotDate) return false;
      const now = dayjs();
      return now.isAfter(slotDate.subtract(5, 'minute')) && now.isBefore(slotDate.add(1, 'hour'));
    } catch {
      return false;
    }
  };

  const normalizeSignsList = (raw) => {
    if (Array.isArray(raw)) return raw.filter(Boolean).map((s) => (s && typeof s === 'object' ? { ...s } : s));
    if (raw && typeof raw === 'object') {
      return Object.values(raw)
        .filter((s) => s && typeof s === 'object')
        .map((s) => ({ ...s }));
    }
    return [];
  };

  const applyTargetingToSigns = (signs, orderAdunitId, { allowAppend } = {}) => {
    const list = normalizeSignsList(signs);
    const effectiveAdunitId = String(orderAdunitId || '').trim() || getAdunitIdFromSign(list[0]) || '';
    let found = false;

    if (effectiveAdunitId) {
      for (const sign of list) {
        if (getAdunitIdFromSign(sign) === effectiveAdunitId) {
          sign.isTargeted = true;
          found = true;
        }
      }
      if (!found && list.length === 1) {
        list[0].isTargeted = true;
        found = true;
      }
      if (!found && allowAppend) {
        list.push({ adunit_id: effectiveAdunitId, isTargeted: true });
        found = true;
      }
    } else if (list.length === 1) {
      list[0].isTargeted = true;
      found = true;
    }

    return { signs: list, found };
  };

  // Match iOS activation: set signs[].isTargeted=true on takeovers/ and active_dsp_campaigns/.
  const updateDspTargetingOnActivate = async (campaignId, { required } = {}) => {
    const orderAdunitId = String(takeover?.adunit_id || takeover?.adunitId || '').trim();
    const activeRef = doc(db, 'active_dsp_campaigns', campaignId);
    const takeoverRef = doc(db, 'takeovers', campaignId);
    const [activeSnap, takeoverSnap] = await Promise.all([getDoc(activeRef), getDoc(takeoverRef)]);

    if (required && !activeSnap.exists()) {
      throw new Error(`DSP campaign isn't active yet (missing active_dsp_campaigns/${campaignId}).`);
    }

    const stamp = {
      updatedAt: serverTimestamp(),
      targetingActivatedAt: serverTimestamp(),
      isActivated: true,
      status: 'active',
    };

    if (activeSnap.exists()) {
      const activeSigns = normalizeSignsList(activeSnap.data()?.signs);
      if (required && activeSigns.length === 0) {
        throw new Error('Active DSP campaign has no signs attached.');
      }
      const { signs: nextActiveSigns, found } = applyTargetingToSigns(activeSigns, orderAdunitId, { allowAppend: false });
      if (required && !found) {
        throw new Error('Could not match adunit_id to target in active DSP campaign.');
      }
      await setDoc(activeRef, { ...stamp, ...(found ? { signs: nextActiveSigns } : {}) }, { merge: true });
    } else {
      await setDoc(activeRef, stamp, { merge: true });
    }

    const takeoverSigns = normalizeSignsList(takeoverSnap.exists() ? takeoverSnap.data()?.signs : []);
    const { signs: nextTakeoverSigns, found: takeoverFound } = applyTargetingToSigns(
      takeoverSigns,
      orderAdunitId,
      { allowAppend: true }
    );
    await setDoc(
      takeoverRef,
      { ...stamp, ...(takeoverFound ? { signs: nextTakeoverSigns } : {}) },
      { merge: true }
    );
  };

  const performActivation = async () => {
    setActivationLoading(true);
    setActivationError('');
    setActivationSuccess(false);

    const isSpotlight = !!takeover?.isSpotlight || String(takeover?.signID || '').toUpperCase() === 'SPOTLIGHT';
    const orderCollection = isSpotlight ? 'spotlights' : 'orders';
    const triggerId = String(takeover?.triggerId || '').trim();
    const campaignId = String(dspCampaignId || takeover?.takeoverDspCampaignId || '').trim();

    try {
      if (triggerId) {
        const res = await fetch(`${getAdminApiBase()}/api/neuron/activate-trigger`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ triggerId }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.success !== true) {
          throw new Error(json.error || 'Failed to activate Neuron trigger.');
        }
        await updateDoc(doc(db, orderCollection, takeover.id), { activated: true });
        if (campaignId) {
          try {
            await updateDspTargetingOnActivate(campaignId, { required: false });
          } catch (e) {
            console.warn('DSP targeting update failed after Neuron activate:', e);
          }
        }
      } else if (campaignId) {
        await updateDspTargetingOnActivate(campaignId, { required: true });
        await updateDoc(doc(db, orderCollection, takeover.id), { activated: true });
      } else {
        throw new Error("This takeover doesn't have an activation trigger or DSP campaign id.");
      }

      setIsActivated(true);
      setActivationSuccess(true);
      if (refreshTakeover) refreshTakeover();
    } catch (e) {
      setActivationError(e.message || 'Activation failed');
    } finally {
      setActivationLoading(false);
    }
  };

  const syncHoursAndSlots = (hours, slots) => {
    const nextHours = uniqueSortedHours(hours);
    setEditSelectedHours(nextHours);
    if (Array.isArray(slots)) {
      setEditTimeSlots(slots);
    } else {
      const extras = editTimeSlots.filter((slot) => parseHourFromSlot(slot) == null);
      setEditTimeSlots([...nextHours.map(formatHourLabel), ...extras]);
    }
  };

  const toggleSelectedHour = (hour) => {
    const nextHours = editSelectedHours.includes(hour)
      ? editSelectedHours.filter((h) => h !== hour)
      : uniqueSortedHours([...editSelectedHours, hour]);
    syncHoursAndSlots(nextHours);
  };

  const handleAddTimeSlot = () => {
    const t = prompt('Time (HH:MM AM/PM)');
    if (!t) return;
    const hour = parseHourFromSlot(t);
    const nextSlots = [...editTimeSlots, t];
    const nextHours = hour == null ? editSelectedHours : uniqueSortedHours([...editSelectedHours, hour]);
    syncHoursAndSlots(nextHours, nextSlots);
  };

  const handleRemoveTimeSlot = (index) => {
    const removed = editTimeSlots[index];
    const nextSlots = editTimeSlots.filter((_, idx) => idx !== index);
    const removedHour = parseHourFromSlot(removed);
    const nextHours = removedHour == null
      ? editSelectedHours
      : editSelectedHours.filter((h) => h !== removedHour);
    syncHoursAndSlots(nextHours, nextSlots);
  };

  const handleSaveDateTime = async () => {
    setLoading(true);
    try {
      const ref = doc(db, 'orders', takeover.id);
      await updateDoc(ref, {
        date: dayjs(editDate).format('MMMM D, YYYY'),
        timeSlots: editTimeSlots,
        selectedHours: editSelectedHours
      });
      alert('Schedule updated');
      if (refreshTakeover) refreshTakeover();
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSubmittedCreatives = async () => {
    if (!takeover?.id) return;
    const info = getCreativeDetailsInfo(takeover);
    const urls = info?.uniqueUrls || [];
    if (!urls.length) return;

    setCreativeDownloadLoading(true);
    setCreativeDownloadProgress('Preparing zip…');
    try {
      const headers = await analyticsAuthHeaders();
      const res = await fetch(
        `${getAdminApiBase()}/api/orders/${encodeURIComponent(String(takeover.id))}/submitted-assets/zip`,
        { method: 'POST', headers }
      );
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.error || json.message || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      if (!blob || blob.size < 22) {
        throw new Error('The zip file was empty.');
      }
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const signPart = sanitizeZipBaseName(takeover?.signName || takeover?.id);
      const disposition = res.headers.get('content-disposition') || '';
      const named = /filename="?([^"]+)"?/i.exec(disposition);
      link.href = objectUrl;
      link.download = named?.[1] || `${signPart}-submitted-assets.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    } catch (e) {
      alert(e?.message || 'Failed to download submitted photos.');
    } finally {
      setCreativeDownloadLoading(false);
      setCreativeDownloadProgress('');
    }
  };

  const handleApproveCreative = async (key) => {
    // Non-DSP legacy behavior: approve inside orders.creativeDetails
    setApproveLoading(true);
    try {
      const ref = doc(db, 'orders', takeover.id);
      await updateDoc(ref, {
        [`creativeDetails.${key}.status`]: 'Approved',
        status: 'Approved'
      });
      setStatus('Approved');
      if (refreshTakeover) refreshTakeover();
      // Keep notification behavior consistent with the "Approve" action button
      sendStatusPushNotification({ newStatus: 'Approved' });
    } finally {
      setApproveLoading(false);
    }
  };

  const handleSubmitDspCreative = async (creative) => {
    if (!isDspOrder) return;
    if (!dspCampaignId) {
      alert('Missing takeoverDspCampaignId on order.');
      return;
    }
    if (!dspCampaign) {
      alert('DSP campaign not loaded yet.');
      return;
    }

    const creativeDocId = creative?.id || creative?.creative_id || creative?.creativeId;
    const assetUrl = creative?.croppedImageUrl || creative?.croppedUrl || creative?.originalImageUrl;
    const type = creative?.type || (isVideo(assetUrl) ? 'video' : 'image');
    const { width, height } = parseDimensions(creative?.dimensions || creative?.format);
    const draftAdvertiser = String(dspAdvertiserUrlDraft || '').trim();
    const advertiserDomain = getAdvertiserDomainFromCampaign(
      draftAdvertiser ? { ...dspCampaign, clientWebsite: draftAdvertiser } : dspCampaign
    );
    const normalizedKey = creative?.__forceNormalizedKey || getNormalizedKeyForCreative(creative);
    const selectedIabCode = creative?.__forceIab || iabCategoryByFormat?.[normalizedKey];
    const iab_categories = selectedIabCode ? [String(selectedIabCode).trim()] : null;
    const sellerId = pickPublisherIdFromCampaign(dspCampaign);

    if (!creativeDocId) {
      alert('Missing creative id (creative doc id) for this DSP creative.');
      return;
    }
    if (!assetUrl) {
      alert('Missing creative asset URL (croppedImageUrl/originalImageUrl).');
      return;
    }
    if (!advertiserDomain) {
      alert('Missing advertiser domain on DSP takeover record (clientWebsite/clientDomain/advertiserDomain needed).');
      return;
    }
    if (!sellerId) {
      alert('Missing seller_id on DSP takeover signs[0].');
      return;
    }
    // Match CreateCampaignView.vue behavior: skip if publisher id isn't HS-XX-<int>
    if (!isValidHivestackSellerId(sellerId)) {
      alert(`Invalid publisher id for DSP creative submission: "${sellerId}" (expected HS-XX-<int>).`);
      return;
    }
    // Match CreateCampaignView.vue: require IAB category before DSP submission
    // Skip IAB picker when re-adding an already-approved creative to the campaigns list
    if (!iab_categories && !creative?.__isReadd) {
      setPendingDspSubmitCreative(creative);
      openIabPicker({ normalizedKey });
      return;
    }

    setDspSubmittingCreativeId(creativeDocId);
    setApproveLoading(true);
    try {
      // Persist IAB selection onto the DSP takeover record for ops visibility
      // (and mirror it to active_dsp_campaigns as well), matching the "campaign stores creative classification" expectation.
      try {
        const iabCode = iab_categories?.[0] || null;
        const safeCreativeKey = safeFirestoreKey(creativeDocId);
        const safeFormatKey = safeFirestoreKey(normalizedKey);
        const iabPersist = {
          updatedAt: new Date(),
          ...(iabCode ? { [`iabCategoryByCreative.${safeCreativeKey}`]: iabCode } : {}),
          ...(iabCode ? { [`iabCategoryByFormat.${safeFormatKey}`]: iabCode } : {})
        };
        // Use merge so we don't clobber campaign fields
        await setDoc(doc(db, 'takeovers', dspCampaignId), iabPersist, { merge: true });
        await setDoc(doc(db, 'active_dsp_campaigns', dspCampaignId), iabPersist, { merge: true });

        // Warm-up too (only if it already exists)
        const warmupId = `warmup-${dspCampaignId}`;
        try {
          const warmSnap = await getDoc(doc(db, 'takeovers', warmupId));
          if (warmSnap.exists()) {
            await setDoc(doc(db, 'takeovers', warmupId), iabPersist, { merge: true });
            await setDoc(doc(db, 'active_dsp_campaigns', warmupId), iabPersist, { merge: true });
          }
        } catch (e) {
          console.warn('Warm-up IAB mirror failed (continuing):', e);
        }
      } catch (e) {
        console.warn('Failed to persist IAB selection onto takeover campaign (continuing):', e);
      }

      // If already submitted, allow operator to proceed with linking/record creation without re-submitting.
      // IMPORTANT: If a seller-scoped id already exists in dspCreativeIdMap, we must reuse it and never generate a VUE-temp id
      // unless the operator explicitly chooses "resubmit".
      let skipPerionSubmit = !!creative?.__isReadd;
      let existingCreativeData = null;
      let existingCreativeExists = false;
      try {
        const existingSnap = await getDoc(doc(db, 'creatives', creativeDocId));
        if (existingSnap.exists()) {
          existingCreativeExists = true;
          existingCreativeData = existingSnap.data();
        }
      } catch (e) {
        console.warn('Could not read existing creative doc for pre-check:', e);
      }

      // IMPORTANT: Match CreateCampaignView.vue behavior: don't treat `statuses` as proof of vendor submission.
      // Use seller-scoped dspCreativeIdMap if present. If it exists, prefer it and do NOT generate temp ids.
      const existingIdForSeller = getCreativeDspIdForSeller(existingCreativeData, sellerId);
      const hasMappedId = !!String(existingIdForSeller || '').trim();

      // Match CreateCampaignView.vue "library submit" behavior:
      // - Use deterministic creative_id per publisher: VUE-CL-<publisher>_<firebaseCreativeId>
      // - Use `publisher_id` (NOT `seller_id`)
      // - Best-effort: if deterministic id already exists in Hivestack, just link it (no re-submit).
      const generateLibraryDspCreativeId = (firebaseCreativeId, publisherId) => {
        const pub = String(publisherId || '').trim();
        return `VUE-CL-${pub}_${String(firebaseCreativeId || '').trim()}`;
      };
      const generateDspCreativeIdForSeller = (tempCampaignId, normalizedKeyIn, sellerIdIn) => {
        const rand = Math.random().toString(36).slice(2, 9);
        return `VUE-${tempCampaignId}-${normalizedKeyIn}-${String(sellerIdIn || '').trim()}_${Date.now()}_${rand}`;
      };

      const candidateId = creativeDocId ? generateLibraryDspCreativeId(creativeDocId, sellerId) : '';
      let skipPerionSubmitBecauseExists = false;
      let forceResubmitNewId = false;
      // Prefer existing seller-scoped id if present (this is the key fix).
      // Only fall back to candidateId if map is missing.
      let perionCreativeIdToUse = String(existingIdForSeller || '').trim() || candidateId || creativeDocId;
      let perionExistsForId = false;

      // If the pre-check modal requested "Submit Anyway", bypass the deterministic id and submit a fresh unique id.
      if (existingCreativeData?.__forceResubmit) {
        forceResubmitNewId = true;
        skipPerionSubmitBecauseExists = false;
        perionCreativeIdToUse = generateDspCreativeIdForSeller(`temp_${Date.now()}`, normalizedKey, sellerId);
      }
      // Existence check: if Perion already has this id for this publisher, don't re-submit.
      // If it doesn't exist yet, we'll submit using the SAME id (no temp ids unless operator resubmits).
      if (perionCreativeIdToUse) {
        try {
          const checkRes = await fetch(
            `${PERION_API_BRIDGE_BASE}/api/creatives/${encodeURIComponent(perionCreativeIdToUse)}?seller_id=${encodeURIComponent(String(sellerId || '').trim())}`
          );
          if (checkRes.ok) {
            const checkData = await checkRes.json().catch(() => null);
            const items = checkData || [];
            const exists = Array.isArray(items) ? items.length > 0 : !!items;
            if (exists) {
              perionExistsForId = true;
              // Default behavior: if we already have a mapped id (dspCreativeIdMap) and Perion confirms it exists,
              // silently link and proceed without re-submitting (prevents duplicates).
              // If there is no mapped id (rare), keep the existing "choice" UX.
              if (hasMappedId) {
                skipPerionSubmitBecauseExists = true;
              } else {
                const choice = await promptExistsChoice({
                  title: 'Creative Already Exists (Publisher)',
                  body:
                    `Publisher: ${String(sellerId || '').trim()}\n` +
                    `Existing DSP ID: ${perionCreativeIdToUse}\n\n` +
                    `Choose an action:\n` +
                    `- Link Existing: reuse the existing publisher creative (no re-submit)\n` +
                    `- Resubmit Anyway: create a NEW DSP creative id and submit again`
                });
                if (choice === 'cancel') return;
                if (choice === 'resubmit') {
                  forceResubmitNewId = true;
                  perionCreativeIdToUse = generateDspCreativeIdForSeller(`temp_${Date.now()}`, normalizedKey, sellerId);
                  perionExistsForId = false;
                } else {
                  skipPerionSubmitBecauseExists = true;
                }
              }
            }
          }
        } catch {
          // Not found / not indexed yet -> fall through to submission.
        }
      }

      // If we don't have a usable id yet, fall back to the firebase creative id.
      if (!perionCreativeIdToUse) perionCreativeIdToUse = creativeDocId;

      const cleanUrl = String(assetUrl).split('?')[0];
      const ext = cleanUrl.split('.').pop()?.toLowerCase() || 'jpg';
      const formatMap = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        webm: 'video/webm'
      };

      const payload = {
        creative_id: perionCreativeIdToUse,
        name: String((takeover?.id || 'Campaign Creative') || '').substring(0, 100),
        advertiser_domain: advertiserDomain,
        publisher_id: String(sellerId || '').trim(),
        media_files: [{
          url: assetUrl,
          type: ext.includes('mp4') || ext.includes('mov') || ext.includes('webm') ? 'video' : 'image',
          format: formatMap[ext] || guessMimeType(assetUrl, type),
          width: width || 1920,
          height: height || 1080
        }]
      };
      if (iab_categories) payload.iab_categories = iab_categories;

      let data = { skipped: true };
      if (!skipPerionSubmit && !skipPerionSubmitBecauseExists) {
        const res = await fetch(`${PERION_API_BRIDGE_BASE}/api/creatives`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        data = await res.json().catch(() => ({}));
        if (!res.ok || data?.success === false) {
          const msgText = String(data?.message || data?.error || '').toLowerCase();
          const alreadyExists =
            msgText.includes('already exists') ||
            msgText.includes('external creative') && msgText.includes('already exists');

          if (alreadyExists) {
            const choice = await promptExistsChoice({
              title: 'Creative Already Exists (API Response)',
              body:
                `Publisher: ${String(sellerId || '').trim()}\n\n` +
                `Perion responded that this creative already exists.\n\n` +
                `Choose an action:\n` +
                `- Link Existing: proceed without re-submitting\n` +
                `- Resubmit Anyway: create a NEW DSP creative id and submit again`
            });

            if (choice === 'cancel') return;
            if (choice === 'resubmit') {
              const newId = generateDspCreativeIdForSeller(`temp_${Date.now()}`, normalizedKey, sellerId);
              const retryPayload = { ...payload, creative_id: newId };
              try {
                const res2 = await fetch(`${PERION_API_BRIDGE_BASE}/api/creatives`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(retryPayload)
                });
                const data2 = await res2.json().catch(() => ({}));
                if (!res2.ok || data2?.success === false) {
                  throw new Error(data2?.message || data2?.error || `Resubmit failed (${res2.status})`);
                }
                // Switch to the new id as the canonical id for saving.
                perionCreativeIdToUse = newId;
                data = data2;
                forceResubmitNewId = true;
              } catch (e) {
                throw e;
              }
            } else {
              // Link existing (no re-submit)
              skipPerionSubmitBecauseExists = true;
              console.warn('Perion says creative already exists for publisher; proceeding without re-submit:', data);
            }
          } else {
            throw new Error(data?.message || data?.error || `Submit failed (${res.status})`);
          }
        }
      }

      // Perion API Bridge expects `creative_id` to be provided by us (Firebase creative id).
      // Some responses may not return a separate ID; fall back to the submitted creative id.
      // CreateCampaignView uses our creative_id as the DSP id; honor that.
      const perionCreativeId =
        perionCreativeIdToUse ||
        data?.creative_id ||
        data?.creativeId ||
        data?.dsp_creative_id ||
        data?.dspCreativeId ||
        data?.id ||
        existingCreativeData?.dspCreativeId ||
        existingCreativeData?.dspCreativeID ||
        existingCreativeData?.dspId ||
        creativeDocId;

      const didSubmitOrLinkVendor = perionExistsForId || (!skipPerionSubmit && !skipPerionSubmitBecauseExists);

      // Mirror into takeover campaign creativeDetails (similar shape to CreateCampaignView.vue campaigns.creativeDetails)
      // so ops can see the DSP id directly on the takeover record.
      try {
        const safeFormatKey = safeFirestoreKey(normalizedKey);
        const creativeEntryForCampaign = {
          id: creativeDocId,
          creative_id: creativeDocId,
          creativeId: creativeDocId,
          originalImageUrl: assetUrl,
          croppedImageUrl: assetUrl,
          dimensions: (width && height) ? `${width}x${height}` : (creative?.dimensions || creative?.format || ''),
          fileType: type,
          uploadedAt: new Date().toISOString(),
          moderationStatus: 'uploaded',
          statuses: sellerId ? { [sellerId]: 'uploaded' } : {},
          dspCreativeIds: perionCreativeId ? [perionCreativeId] : null,
          dspCreativeIdMap: (sellerId && perionCreativeId) ? { [sellerId]: perionCreativeId } : null,
          sellerIds: sellerId ? [sellerId] : null,
          iab_categories: iab_categories
        };

        const campaignSnap = await getDoc(doc(db, 'takeovers', dspCampaignId));
        const campaignData = campaignSnap.exists() ? (campaignSnap.data() || {}) : {};
        const existingCd = (campaignData?.creativeDetails && typeof campaignData.creativeDetails === 'object')
          ? campaignData.creativeDetails
          : {};
        const existingBucket = existingCd?.[safeFormatKey] && typeof existingCd[safeFormatKey] === 'object'
          ? existingCd[safeFormatKey]
          : { creatives: [], format: normalizedKey, type: 'image' };
        const existingList = Array.isArray(existingBucket?.creatives) ? existingBucket.creatives : [];
        const nextList = [
          ...existingList.filter((c) => String(getCreativeDocIdFromObj(c) || c?.id || '') !== String(creativeDocId)),
          creativeEntryForCampaign
        ];
        const nextBucket = {
          ...existingBucket,
          creatives: nextList,
          format: existingBucket?.format || normalizedKey,
          // Match CreateCampaignView defaulting to 'image', but keep video if we detect it
          type: (String(type || '').toLowerCase() === 'video') ? 'video' : (existingBucket?.type || 'image')
        };

        const creativeDetailsPatch = {
          updatedAt: new Date(),
          [`creativeDetails.${safeFormatKey}`]: nextBucket
        };
        await setDoc(doc(db, 'takeovers', dspCampaignId), creativeDetailsPatch, { merge: true });
        await setDoc(doc(db, 'active_dsp_campaigns', dspCampaignId), creativeDetailsPatch, { merge: true });

        // Warm-up too (only if it already exists)
        const warmupId = `warmup-${dspCampaignId}`;
        try {
          const warmSnap = await getDoc(doc(db, 'takeovers', warmupId));
          if (warmSnap.exists()) {
            await setDoc(doc(db, 'takeovers', warmupId), creativeDetailsPatch, { merge: true });
            await setDoc(doc(db, 'active_dsp_campaigns', warmupId), creativeDetailsPatch, { merge: true });
          }
        } catch (e) {
          console.warn('Warm-up creativeDetails mirror failed (continuing):', e);
        }
      } catch (e) {
        console.warn('Failed to write takeover creativeDetails (continuing):', e);
      }

      // 0) Track pending DSP submission for ops / downstream processing
      await addDoc(collection(db, 'pendingCreatives'), {
        takeover: true,
        firebaseCreativeId: creativeDocId,
        perionCreativeId: perionCreativeId || null,
        sellerId: sellerId || null,
        iab_categories: iab_categories || null,
        userUid: takeover?.userUid || dspCampaign?.userUid || null,
        ordersDocumentId: takeover?.id || null,
        takeoversDocumentId: dspCampaignId || null,
        takeoverOrderId: takeover?.id || null,
        takeoverDspCampaignId: dspCampaignId || null,
        skippedPerionSubmit: !!skipPerionSubmit || !!skipPerionSubmitBecauseExists,
        resubmittedNewDspId: !!forceResubmitNewId,
        perionSubmitPayload: payload,
        perionSubmitResponse: data,
        createdAt: new Date()
      });

      // 0.5) Auto-match Deal IDs (same matching logic as CreateCampaignView)
      // Matches deals where deal.seller_id === any selected sign seller_id
      try {
        await autoMatchDealIdsForCampaign({ campaign: dspCampaign, campaignId: dspCampaignId });
      } catch (e) {
        console.warn('Deal auto-match failed (continuing submission):', e);
      }

      // 1) Update Firestore: creatives/<creativeId> with upload metadata
      // If already submitted/approved, do NOT clobber moderationStatus/statuses back to pending.
      const existingModerationStatus = existingCreativeData?.moderationStatus || '';
      const existingStatuses = existingCreativeData?.statuses || {};
      const existingSellerStatus = sellerId ? (existingStatuses[safeFirestoreKey(sellerId)] || existingStatuses[sellerId]) : null;

      const creativeUpdate = {
        dspSellerId: sellerId,
        dspCampaignId,
        iab_categories: iab_categories,
        updatedAt: new Date()
      };
      // Only set dspSubmittedAt if we actually submitted OR confirmed the id exists in Perion.
      if (didSubmitOrLinkVendor && !existingCreativeData?.dspSubmittedAt) creativeUpdate.dspSubmittedAt = new Date();
      if (!existingModerationStatus && !existingSellerStatus) {
        creativeUpdate.moderationStatus = 'pending';
        creativeUpdate[`statuses.${safeFirestoreKey(sellerId)}`] = 'pending';
      }
      // Firestore updateDoc does NOT allow undefined values
      if (perionCreativeId) creativeUpdate.dspCreativeId = perionCreativeId;
      if (sellerId && perionCreativeId) {
        creativeUpdate[`dspCreativeIdMap.${safeFirestoreKey(sellerId)}`] = perionCreativeId;
      }
      // CreateCampaignView always persists these fields; do the same here (merge, safe if doc doesn't exist yet).
      try {
        await setDoc(doc(db, 'creatives', creativeDocId), creativeUpdate, { merge: true });
      } catch (e) {
        console.warn('Creative doc write failed (continuing):', e);
      }

      // Best-effort: also mirror onto orders/<id>.creativeDetails so admin views that depend on it can show the DSP id.
      try {
        const orderRef = doc(db, 'orders', takeover.id);
        const orderEntry = {
          id: creativeDocId,
          creativeId: creativeDocId,
          creative_id: creativeDocId,
          croppedUrl: assetUrl || '',
          originalUrl: assetUrl || '',
          type,
          dimensions: (width && height) ? `${width}x${height}` : (creative?.dimensions || creative?.format || ''),
          status: 'Pending',
          dspCreativeId: perionCreativeId || null,
          iab_categories: iab_categories || null,
          updatedAt: new Date()
        };
        await updateDoc(orderRef, {
          updatedAt: new Date(),
          [`creativeDetails.${safeFirestoreKey(creativeDocId)}`]: orderEntry
        });
      } catch (e) {
        console.warn('Order creativeDetails mirror failed (continuing):', e);
      }

      // 2) Update orders/<orderId> status => Pending (and keep legacy creativeDetails status in sync if present)
      await updateDoc(doc(db, 'orders', takeover.id), {
        status: 'Pending'
      });
      setStatus('Pending');

      // 3) Update takeovers/<campaignId> status => Pending
      await updateDoc(doc(db, 'takeovers', dspCampaignId), {
        status: 'Pending',
        updatedAt: new Date()
      });

      // 4) Mirror into active_dsp_campaigns/<campaignId> as active + activated
      // IMPORTANT: Use the latest persisted takeover doc as the source of truth (includes updated creativeDetails/DSP ids/IAB),
      // instead of spreading potentially-stale React state (`dspCampaign`) which can overwrite the just-written DSP ids.
      try {
        const latestSnap = await getDoc(doc(db, 'takeovers', dspCampaignId));
        const latest = latestSnap.exists() ? (latestSnap.data() || {}) : (dspCampaign || {});
        await setDoc(doc(db, 'active_dsp_campaigns', dspCampaignId), {
          ...latest,
          ...buildBidParamsFromSign0(latest),
          ...buildScheduleFix(latest),
          status: 'active',
          isActivated: true,
          updatedAt: new Date()
        }, { merge: true });
      } catch (e) {
        console.warn('Active DSP mirror failed (continuing):', e);
      }

      // Ensure the main active takeover has full deal metadata attached as well
      // (dealIds, dealId, deals[], dealName, dealFloorCpm, publisherId).
      try {
        await autoMatchDealIdsForCampaign({ campaign: dspCampaign, campaignId: dspCampaignId });
      } catch (e) {
        console.warn('Deal auto-match (main active takeover) failed:', e);
      }

      // 5) Fetch DSP approval state immediately (best-effort) and persist to creative doc
      if (perionCreativeId) {
        try {
          const statusObj = await fetchDspCreativeStatus(perionCreativeId);
          setDspCreativeStatusByDspId(prev => ({ ...prev, [perionCreativeId]: statusObj }));
          await persistDspCreativeStatusToFirestore({ creativeDocId, dspCreativeId: perionCreativeId, sellerId, statusObj });
        } catch (e) {
          console.warn('DSP status fetch failed (will remain pending):', e);
        }
      }

      // Refresh local DSP creative meta map so UI updates
      setDspCreativeMetaById(prev => ({
        ...prev,
        [creativeDocId]: {
          ...(prev[creativeDocId] || {}),
          dspCreativeId: perionCreativeId || (prev[creativeDocId]?.dspCreativeId || ''),
          dspCampaignId: dspCampaignId,
          dspSubmittedAt: prev[creativeDocId]?.dspSubmittedAt || new Date(),
          moderationStatus: 'pending',
          statuses: { ...(prev[creativeDocId]?.statuses || {}), [safeFirestoreKey(sellerId)]: 'pending' }
        }
      }));

      if (refreshTakeover) refreshTakeover();
      alert(
        creative?.__isReadd
          ? 'Creative re-added to takeovers/ and active_dsp_campaigns.'
          : 'Creative submitted to publisher (DSP). Order moved to Pending and mirrored to active_dsp_campaigns.'
      );
    } catch (e) {
      console.error('DSP creative submission failed:', e);
      alert(`DSP submission failed: ${e.message || 'Unknown error'}`);
    } finally {
      setApproveLoading(false);
      setDspSubmittingCreativeId('');
    }
  };

  const buildCampaignCreativeEntryFromCreativeDoc = ({ creativeDocId, creativeDoc }) => {
    const d = creativeDoc || {};
    const croppedImageUrl = d.croppedImageUrl || d.croppedUrl || d.croppedURL || d.cropped_image_url || '';
    const originalImageUrl = d.originalImageUrl || d.originalUrl || d.originalURL || d.original_image_url || '';
    const type = d.type || (isVideo(croppedImageUrl || originalImageUrl) ? 'video' : 'image');
    const dimensions = d.dimensions || d.format || d.size || '';
    return {
      id: creativeDocId,
      creative_id: creativeDocId, // legacy compatibility
      creativeId: creativeDocId, // legacy compatibility
      croppedImageUrl: croppedImageUrl || undefined,
      croppedUrl: croppedImageUrl || undefined,
      originalImageUrl: originalImageUrl || undefined,
      type,
      dimensions
    };
  };

  const getCreativeDocIdFromObj = (c) => c?.id || c?.creative_id || c?.creativeId || '';

  const isCampaignLockedForSwaps = (campaign) => {
    if (!campaign) return true;
    // Block swapping if a warm-up has been created or this is itself a warm-up.
    if (campaign?.isWarmup || campaign?.iswarmup) return true;
    if (campaign?.warmupTakeoverId || campaign?.warmupCreatedAt) return true;
    // Block swapping once the campaign has moved into vendor approval / active execution.
    const status = String(campaign?.status || '').toLowerCase();
    if (status === 'pending' || status === 'active') return true;
    if (campaign?.isActivated) return true;
    return false;
  };

  const creativeLooksSubmittedToVendor = (creativeDocData) => {
    const d = creativeDocData || {};
    return (
      !!d.dspSubmittedAt ||
      !!d.dspCreativeId ||
      !!d.dspCreativeID ||
      !!d.dspId
    );
  };

  const buildOrderCreativeDetailsEntryFromCreativeDoc = ({ creativeDocId, creativeDoc }) => {
    const d = creativeDoc || {};
    const croppedUrl = d.croppedImageUrl || d.croppedUrl || d.croppedURL || d.cropped_image_url || '';
    const originalUrl = d.originalImageUrl || d.originalUrl || d.originalURL || d.original_image_url || '';
    const type = d.type || (isVideo(croppedUrl || originalUrl) ? 'video' : 'image');
    const dimensions = d.dimensions || d.format || d.size || '';
    return {
      id: creativeDocId,
      creativeId: creativeDocId,
      creative_id: creativeDocId,
      croppedUrl: croppedUrl || originalUrl || '',
      originalUrl: originalUrl || '',
      type,
      dimensions,
      status: 'New',
      updatedAt: new Date()
    };
  };

  const findCreativeDetailsKeyForCreativeId = (creativeDetails, creativeId) => {
    if (!creativeDetails || typeof creativeDetails !== 'object') return null;
    if (creativeDetails[creativeId]) return creativeId;
    for (const [k, v] of Object.entries(creativeDetails)) {
      if (!v || typeof v !== 'object') continue;
      const id =
        v.id ||
        v.creativeId ||
        v.creative_id ||
        v.firebaseCreativeId ||
        v.firebase_creative_id ||
        '';
      if (String(id) === String(creativeId)) return k;
    }
    return null;
  };

  const handleOpenSwapCreative = (creativeDocId) => {
    setSwapCreativeError('');
    setSwapOldCreativeId(creativeDocId || '');
    setSwapNewCreativeId('');
    setSwapCreativeOpen(true);
  };

  const openCreateCreative = () => {
    setCreateCreativeError('');
    setCreateCreativeFile(null);
    setCreateCreativeDimensions('');
    setCreateCreativeName('');
    setCreateCreativeOpen(true);
  };

  const safeFileName = (name) => String(name || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');

  const handleCreateAndUploadCreative = async () => {
    if (!isDspOrder || !dspCampaignId) return;
    if (!dspCampaign) {
      setCreateCreativeError('DSP campaign not loaded yet.');
      return;
    }
    if (isCampaignLockedForSwaps(dspCampaign)) {
      setCreateCreativeError('This DSP takeover is locked (warm-up created or already in approval/active). Creating/swapping is disabled.');
      return;
    }
    if (!createCreativeFile) {
      setCreateCreativeError('Choose a file to upload.');
      return;
    }

    setCreateCreativeLoading(true);
    setCreateCreativeError('');
    try {
      // Create a deterministic new doc id first (without writing), so we can upload under that path.
      const newRef = doc(collection(db, 'creatives'));
      const newId = newRef.id;

      const file = createCreativeFile;
      const contentType = file?.type || undefined;
      const type = String(file?.type || '').toLowerCase().startsWith('video') ? 'video' : 'image';
      const path = `creatives/${newId}/${Date.now()}_${safeFileName(file?.name || `${newId}`)}`;
      const r = storageRef(storage, path);

      await uploadBytes(r, file, contentType ? { contentType } : undefined);
      const url = await getDownloadURL(r);

      const userId = takeover?.userUid || dspCampaign?.userUid || null;
      const docBody = {
        name: (createCreativeName || '').trim() || newId,
        userId,
        // media
        originalImageUrl: url,
        croppedImageUrl: url,
        type,
        dimensions: (createCreativeDimensions || '').trim(),
        // linkage (safe; does not imply vendor submission)
        dspCampaignId,
        takeoverDspCampaignId: dspCampaignId,
        // timestamps
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await setDoc(newRef, docBody, { merge: true });

      // Prefill swap with the newly created creative id so operator can immediately swap it in.
      setSwapNewCreativeId(newId);
      setCreateCreativeOpen(false);
      alert(`Creative created: ${newId}\n\nNow click "Swap Creative" and confirm to attach it to the takeover.`);
    } catch (e) {
      console.error('Creative upload/create failed:', e);
      setCreateCreativeError(e?.message || 'Creative upload failed.');
    } finally {
      setCreateCreativeLoading(false);
    }
  };

  const handleConfirmSwapCreative = async () => {
    if (!isDspOrder) return;
    if (!dspCampaignId) return;
    if (!swapOldCreativeId) return;
    const newId = String(swapNewCreativeId || '').trim();
    if (!newId) {
      setSwapCreativeError('Enter the new creative doc id.');
      return;
    }
    if (newId === swapOldCreativeId) {
      setSwapCreativeError('New creative id must be different from the current one.');
      return;
    }

    // Hard gate: never allow swaps once warm-up exists or vendor submission/activation happened.
    if (isCampaignLockedForSwaps(dspCampaign)) {
      setSwapCreativeError('This DSP takeover is locked (warm-up created or already in approval/active). Swapping is disabled.');
      return;
    }

    const ok = window.confirm(
      `Swap DSP creative?\n\nOld: ${swapOldCreativeId}\nNew: ${newId}\n\nThis updates takeovers/${dspCampaignId} assigned_creatives + creatives/${swapOldCreativeId} + creatives/${newId}.`
    );
    if (!ok) return;

    setSwapCreativeLoading(true);
    setSwapCreativeError('');
    try {
      // Re-load campaign fresh to avoid clobbering concurrent edits.
      const campaignSnap = await getDoc(doc(db, 'takeovers', dspCampaignId));
      if (!campaignSnap.exists()) throw new Error(`DSP takeover doc not found: takeovers/${dspCampaignId}`);
      const campaign = { id: campaignSnap.id, ...campaignSnap.data() };
      if (isCampaignLockedForSwaps(campaign)) throw new Error('DSP takeover is locked (warm-up created or already in approval/active).');

      // Load creative docs (both must exist).
      const [oldSnap, newSnap] = await Promise.all([
        getDoc(doc(db, 'creatives', swapOldCreativeId)),
        getDoc(doc(db, 'creatives', newId))
      ]);
      if (!oldSnap.exists()) throw new Error(`Old creative doc not found: creatives/${swapOldCreativeId}`);
      if (!newSnap.exists()) throw new Error(`New creative doc not found: creatives/${newId}`);
      const oldCreativeData = oldSnap.data();
      const newCreativeData = newSnap.data();

      if (creativeLooksSubmittedToVendor(oldCreativeData)) {
        throw new Error('Old creative has already been submitted to vendor (dspSubmittedAt/dspCreativeId set). Swap blocked.');
      }
      if (creativeLooksSubmittedToVendor(newCreativeData)) {
        throw new Error('New creative has already been submitted to vendor (dspSubmittedAt/dspCreativeId set). Choose an unsubmitted creative.');
      }

      // Update takeover doc: assigned_creatives/assignedCreatives and creatives[] (if present).
      const assigned =
        (campaign?.assignedCreatives && typeof campaign.assignedCreatives === 'object' && campaign.assignedCreatives) ||
        (campaign?.assigned_creatives && typeof campaign.assigned_creatives === 'object' && campaign.assigned_creatives) ||
        {};

      const newEntry = buildCampaignCreativeEntryFromCreativeDoc({ creativeDocId: newId, creativeDoc: newCreativeData });

      let found = false;
      const updatedAssigned = {};
      for (const [k, arr] of Object.entries(assigned || {})) {
        const list = Array.isArray(arr) ? arr : [];
        const replaced = list.map((it) => {
          const id = getCreativeDocIdFromObj(it);
          if (id && id === swapOldCreativeId) {
            found = true;
            return newEntry;
          }
          return it;
        });
        updatedAssigned[k] = replaced;
      }

      const creativesArr = Array.isArray(campaign?.creatives) ? campaign.creatives : [];
      let updatedCreativesArr = creativesArr;
      if (creativesArr.length > 0) {
        updatedCreativesArr = creativesArr.map((it) => {
          const id = getCreativeDocIdFromObj(it);
          if (id && id === swapOldCreativeId) {
            found = true;
            return newEntry;
          }
          return it;
        });
      }

      if (!found) {
        throw new Error('Could not find the old creative id inside assigned_creatives/assignedCreatives (or creatives[]). Nothing was changed.');
      }

      await updateDoc(doc(db, 'takeovers', dspCampaignId), {
        assignedCreatives: updatedAssigned,
        assigned_creatives: updatedAssigned,
        ...(creativesArr.length > 0 ? { creatives: updatedCreativesArr } : {}),
        updatedAt: new Date()
      });

      // Update orders/<orderId>.creativeDetails so admin list/notifications can show the swapped creative.
      // (DSP orders normally don't use creativeDetails, but the admin UI does in a few places.)
      try {
        const orderRef = doc(db, 'orders', takeover.id);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const orderData = orderSnap.data() || {};
          const existingCreativeDetails = orderData?.creativeDetails && typeof orderData.creativeDetails === 'object'
            ? orderData.creativeDetails
            : null;

          const orderEntry = buildOrderCreativeDetailsEntryFromCreativeDoc({ creativeDocId: newId, creativeDoc: newCreativeData });
          const keyToReplace = findCreativeDetailsKeyForCreativeId(existingCreativeDetails, swapOldCreativeId);

          // If there is an existing key (possibly an ad-format key), overwrite that key.
          // If it was keyed by old creative id, delete it and re-key to the new id.
          const updates = { updatedAt: new Date() };
          if (keyToReplace) {
            if (keyToReplace === swapOldCreativeId) {
              updates[`creativeDetails.${swapOldCreativeId}`] = deleteField();
              updates[`creativeDetails.${newId}`] = orderEntry;
            } else {
              updates[`creativeDetails.${keyToReplace}`] = orderEntry;
            }
          } else {
            // No creativeDetails yet; add one keyed by the new creative id.
            updates[`creativeDetails.${newId}`] = orderEntry;
          }
          await updateDoc(orderRef, updates);
        }
      } catch (e) {
        console.warn('Order creativeDetails update failed (continuing):', e);
      }

      // Update creative docs: detach old, attach new (best-effort; keep safe).
      const detachOld = {};
      // Only detach if it points at this campaign (avoid nuking some other linkage)
      if (String(oldCreativeData?.dspCampaignId || '') === String(dspCampaignId)) {
        detachOld.dspCampaignId = deleteField();
      }
      if (String(oldCreativeData?.dspSellerId || '') && String(oldCreativeData?.dspCampaignId || '') === String(dspCampaignId)) {
        detachOld.dspSellerId = deleteField();
      }
      detachOld.updatedAt = new Date();

      const attachNew = {
        dspCampaignId,
        updatedAt: new Date()
      };

      try {
        await Promise.all([
          updateDoc(doc(db, 'creatives', swapOldCreativeId), detachOld),
          updateDoc(doc(db, 'creatives', newId), attachNew)
        ]);
      } catch (e) {
        console.warn('Creative doc linkage update had an issue (campaign updated successfully):', e);
      }

      // Refresh local state for immediate UI feedback.
      setDspCampaign((prev) => prev ? ({
        ...prev,
        assignedCreatives: updatedAssigned,
        assigned_creatives: updatedAssigned,
        ...(creativesArr.length > 0 ? { creatives: updatedCreativesArr } : {}),
        updatedAt: new Date()
      }) : prev);
      setDspCreativeMetaById((prev) => {
        const next = { ...prev };
        delete next[swapOldCreativeId];
        next[newId] = next[newId] || { dspCreativeId: '', moderationStatus: '', statuses: {} };
        return next;
      });

      setSwapCreativeOpen(false);
      setSwapOldCreativeId('');
      setSwapNewCreativeId('');
      alert('DSP creative swapped successfully.');
      if (refreshTakeover) refreshTakeover();
    } catch (e) {
      console.error('Swap creative failed:', e);
      setSwapCreativeError(e.message || 'Swap failed.');
    } finally {
      setSwapCreativeLoading(false);
    }
  };

  const toLocalDateTimeInputValue = (value) => {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const getTakeoverPhotoOpSignKey = () => {
    return String(
      takeover?.adunit_id ||
      takeover?.signId ||
      takeover?.openRTBUUID ||
      takeover?.openrtb_uuid ||
      takeover?.programmatic_platform_key ||
      ''
    ).trim();
  };

  const getPhotoOpDefaultName = () => {
    const signName = String(takeover?.signName || 'Takeover').trim();
    return `Photo Op - ${signName}`;
  };

  const generatePhotoOpInstructions = () => {
    const signName = takeover?.signName || '[Sign Name]';
    const address = takeover?.address || '[Address]';
    const signImgURL = takeover?.signImgURL || '[Board Image URL]';
    const resolution = takeover?.signResolution || '[Resolution]';
    const dma = takeover?.dma || '[DMA]';
    
    // Get creative URLs
    let creativeUrls = [];
    if (takeover?.creativeDetails) {
      creativeUrls = Object.values(takeover.creativeDetails).map(c => 
        c?.croppedUrl || c?.croppedImageUrl || c?.originalImageUrl || ''
      ).filter(Boolean);
    }
    if (creativeUrls.length === 0) {
      const info = getCreativeDetailsInfo(takeover);
      if (info?.uniqueUrls?.length) creativeUrls = info.uniqueUrls;
    }
    const creativeUrlsText = creativeUrls.length > 0 
      ? creativeUrls.map((url, i) => `Creative ${i + 1}: ${url}`).join('\n')
      : '[Creative Image URL]';

    return `Photo and Video Requirements:
(Note: These are required and non negotiable. Failure to meet any of the requirements listed here will result in returned or rejected photos.)
• Use the 'Check In/ Check Out' feature on each job. Check in when you get on-site and click check-out when you're done.
• No rain or adverse weather unless otherwise instructed.
• Minimum 10 megapixel camera.
• Make sure your lens and sensors are clean.
• Set quality of picture to Best setting.
• The object you are shooting must be in focus.
• Landscape ONLY will be accepted.
• Shutter Speed must be set to 1/60 or slower or photos risk being rejected.
• No digital zoom permitted, only optical zoom. Submissions using digital zoom will be rejected.
• Keep the sun overhead or behind you, never behind the object you are taking a photo of.
• No reflections off dashboards and windows. Pictures containing obstructions may be declined.
• Don't leave the site until you check your work to make sure you got what you need. Take extra shots to be certain.
• No editing, color correcting, or cropping.
• Photos must be a minimum of 2MB each.
• Stay safe and lawful, don't take risks.
• Video MUST be stabilized.
• Please use a tripod, monopod, or other stabilization methods to produce stable video.
• Shaky video will be rejected.
• If you do not see the ad you should be shooting, please take video of the board/location and submit those, leaving a comment detailing what you saw.
• If there are any complications preventing you from taking the pictures needed for this job, take pictures of identifying markers of where you are (street signs, building address). FotoFetch completes jobs by purchasing photos, so we need to have something to purchase in order to pay you if the circumstances lead to this situation.

Description:
You are taking several photos and videos of a digital billboard while capturing as much TRAFFIC as possible traveling at the board to demonstrate to our customer how drivers see the billboard.
Getting the right photo requires waiting for the right ad to appear and taking the picture at the correct shutter speed.

ACTIVATION:
PHOTOGRAPHER WILL BE PROVIDED ACTIVATION LINK TO ACTIVATE THE BOARD

Billboard Information:
• Unit Name: ${signName}
• Address: ${address}
• DMA: ${dma}
• Resolution: ${resolution}
• Board Image URL: ${signImgURL}

Creative Information:
${creativeUrlsText}

Video Request:
• Take a close up video of the ad playing.
• Take a video further away showing the environment while the ad plays.

Photo Request:
• Take 5 Approach shots
• Take 2 Close-Up shots of the ad

Shot Types:
A CLOSE UP shot is of the billboard itself and a minimal amount of the structure and the space around the sign.

APPROACH shots typically include the billboard in the left or right third with the cars driving toward it, showing how drivers see the billboard.

PRO TIP: 
• Use a slower shutter speed.
• Keep your camera level to the digital board to avoid MOIRE patterns in your photos.
• Learn to set up your camera for Digital Board photos!

You can always contact FotoFetch via the in-app comments.`;
  };

  const handleCopyPhotoOpInstructions = async () => {
    try {
      const instructions = generatePhotoOpInstructions();
      await navigator.clipboard.writeText(instructions);
      setCopyPhotoOpSuccess(true);
      setTimeout(() => setCopyPhotoOpSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };

  const initializePhotoOpFormDefaults = () => {
    const base = takeover?.date ? new Date(takeover.date) : new Date();
    if (Number.isNaN(base.getTime())) base.setTime(Date.now());
    const start = new Date(base);
    start.setHours(9, 0, 0, 0);
    const end = new Date(start.getTime() + 3 * 60 * 60 * 1000);
    setPhotoOpNameEdited(false);
    setPhotoOpBudgetEdited(false);
    setPhotoOpWindowStart(toLocalDateTimeInputValue(start));
    setPhotoOpWindowEnd(toLocalDateTimeInputValue(end));
    setPhotoOpTotalPlays(PHOTO_OP_DEFAULT_PLAYS);
    setPhotoOpName(getPhotoOpDefaultName());
    const defaultBudget = Number(takeover?.totalPrice || 0);
    setPhotoOpBudget(defaultBudget > 0 ? defaultBudget : 50);
    setPhotoOpError('');
  };

  const analyticsAuthHeaders = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    const token = await user.getIdToken();
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const loadPhotoOpSessions = async () => {
    if (!takeover?.id) return;
    setPhotoOpSessionsLoading(true);
    try {
      const headers = await analyticsAuthHeaders();
      const res = await fetch(`${getAdminApiBase()}/api/orders/${encodeURIComponent(String(takeover.id))}/photo-op-sessions`, {
        headers,
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json?.success) {
        setPhotoOpSessionList(Array.isArray(json?.data?.sessions) ? json.data.sessions : []);
      }
    } catch (e) {
      console.warn('Failed to load photo op sessions:', e);
    } finally {
      setPhotoOpSessionsLoading(false);
    }
  };

  const openPhotoOpModal = () => {
    initializePhotoOpFormDefaults();
    setShowPhotoOpModal(true);
    void loadPhotoOpSessions();
  };

  const getPhotoOpDisplayLabel = (entry) => String(entry?.name || entry?.signLabel || 'Photo op').trim();

  const copyTextToClipboard = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      alert(successMessage);
    } catch {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert(successMessage);
      } catch {
        alert('Failed to copy');
      }
    }
  };

  const deletePhotoOpSession = async (sessionId, displayLabel) => {
    if (!takeover?.id || !sessionId) return;
    const label = String(displayLabel || 'this photo op').trim();
    if (!window.confirm(`Delete ${label}? The photographer link will stop working.`)) return;

    setPhotoOpDeletingSessionId(sessionId);
    setPhotoOpError('');
    try {
      const headers = await analyticsAuthHeaders();
      const res = await fetch(
        `${getAdminApiBase()}/api/orders/${encodeURIComponent(String(takeover.id))}/photo-op-sessions/${encodeURIComponent(sessionId)}`,
        { method: 'DELETE', headers }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        const msg = json?.error || 'Failed to delete photo op session';
        setPhotoOpError(msg);
        alert(msg);
        return;
      }
      setPhotoOpCreatedLinks((prev) => prev.filter((entry) => entry.sessionId !== sessionId));
      setPhotoOpSessionList((prev) => prev.filter((sess) => sess.sessionId !== sessionId));
      alert('Photo op session deleted');
    } catch (e) {
      const msg = e?.message || 'Failed to delete photo op session';
      setPhotoOpError(msg);
      alert(msg);
    } finally {
      setPhotoOpDeletingSessionId('');
    }
  };

  const createPhotoOpLink = async () => {
    if (!takeover?.id) return;
    const signKey = getTakeoverPhotoOpSignKey();
    if (!signKey) {
      alert('This takeover order is missing a sign id (adunit_id / signId)');
      return;
    }
    if (!takeover?.takeoverDspCampaignId) {
      alert(
        'This takeover has no DSP campaign linked (takeoverDspCampaignId).\n\n' +
        'Photo ops need the DSP takeover deals/creatives to play. Submit/create the DSP campaign on this order first, then try again.'
      );
      return;
    }
    if (!photoOpWindowStart || !photoOpWindowEnd) {
      alert('Select a start and end window');
      return;
    }

    const windowStartDate = new Date(photoOpWindowStart);
    const windowEndDate = new Date(photoOpWindowEnd);
    if (Number.isNaN(windowStartDate.getTime()) || Number.isNaN(windowEndDate.getTime())) {
      alert('Please enter valid date/time values');
      return;
    }
    if (windowEndDate <= windowStartDate) {
      alert('End date/time must be after start date/time');
      return;
    }

    const budget = Number(photoOpBudget || 0);
    const totalPlays = Math.max(1, Math.floor(Number(photoOpTotalPlays) || PHOTO_OP_DEFAULT_PLAYS));
    if (!(budget > 0)) {
      alert('Enter a valid photo op budget');
      return;
    }

    setPhotoOpLoading(true);
    setPhotoOpError('');
    try {
      const headers = await analyticsAuthHeaders();
      const res = await fetch(`${getAdminApiBase()}/api/orders/${encodeURIComponent(String(takeover.id))}/photo-op-link`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: String(photoOpName || '').trim(),
          signKey,
          windowStart: windowStartDate.toISOString(),
          windowEnd: windowEndDate.toISOString(),
          totalPlays,
          totalBudget: budget,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        const msg = json?.error || 'Failed to create photo op link';
        setPhotoOpError(msg);
        alert(msg);
        return;
      }

      const link = String(json?.data?.linkUrl || '').trim();
      const pin = String(json?.data?.displayPin || '').trim();
      const sessionId = String(json?.data?.sessionId || '').trim();
      const signSummary = json?.data?.sign || null;
      const signLabel = String(signSummary?.assetName || signSummary?.name || takeover.signName || signKey).trim();
      const sessionName = String(json?.data?.name || photoOpName || signLabel).trim();

      setPhotoOpCreatedLinks((prev) => [
        {
          sessionId,
          name: sessionName,
          linkUrl: link,
          pin,
          signLabel,
          windowStart: String(json?.data?.windowStart || windowStartDate.toISOString()),
          windowEnd: String(json?.data?.windowEnd || windowEndDate.toISOString()),
          createdAt: new Date().toISOString(),
        },
        ...prev.filter((entry) => entry.sessionId !== sessionId),
      ]);

      void loadPhotoOpSessions();
      alert('Photo op link generated — copy the link and PIN below');
    } catch (e) {
      const msg = e?.message || 'Failed to create photo op link';
      setPhotoOpError(msg);
      alert(msg);
    } finally {
      setPhotoOpLoading(false);
    }
  };

  const formatCurrency = (val) => Number(val || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const isVideo = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || 
           lower.includes('.mov') || 
           lower.includes('.webm') || 
           lower.includes('.m4v') ||
           lower.includes('video') || 
           lower.includes('blob:');
  };

  const liveBidPieData = useMemo(() => (
    LIVE_BID_LABELS.map((slice) => ({ ...slice, value: Number(liveBidStats[slice.key] || 0) }))
  ), [liveBidStats.playouts, liveBidStats.pending, liveBidStats.losses, liveBidStats.noBids, liveBidStats.bids]);

  const liveBidTotal = useMemo(
    () => LIVE_BID_LABELS.reduce((sum, slice) => sum + Number(liveBidStats[slice.key] || 0), 0),
    [liveBidStats.playouts, liveBidStats.pending, liveBidStats.losses, liveBidStats.noBids, liveBidStats.bids]
  );

  const liveBidWinRate = liveBidTotal > 0 ? Math.round((Number(liveBidStats.playouts || 0) / liveBidTotal) * 100) : 0;
  const showLiveBidChart = isDspOrder && (isActivated || !!dspCampaign?.isActivated || liveBidTotal > 0 || hasBidChartData);

  useEffect(() => {
    if (liveBidTotal > 0) setHasBidChartData(true);
  }, [liveBidTotal]);

  if (!takeover) return null;

  const canSwapDspCreatives =
    isDspOrder &&
    !!dspCampaign &&
    !dspCampaignLoading &&
    !isCampaignLockedForSwaps(dspCampaign);

  const creativeInfo = getCreativeDetailsInfo(takeover);
  const targetPlays = Number(takeover?.numberOfPlays);
  const hasTargetPlays = Number.isFinite(targetPlays) && targetPlays > 0;
  const playsPerHour = Number(takeover?.playsPerHour);
  const hasPlaysPerHour = Number.isFinite(playsPerHour) && playsPerHour > 0;
  const flipTypeLabel = String(takeover?.flipType || '').trim();
  const flipCount = getFlipCountFromType(flipTypeLabel);
  const isDailyTakeover = takeover?.dailyTakeover === true || /daily/i.test(flipTypeLabel);
  const isSevenFlips = flipCount === 7;
  const showDailyPlayFields = isDailyTakeover || isSevenFlips;
  const displayedTargetPlays = hasTargetPlays ? targetPlays : (isSevenFlips ? 7 : 0);
  const estimatedPlays = hasPlaysPerHour && editSelectedHours.length > 0
    ? editSelectedHours.length * playsPerHour
    : null;

  return (
    <div className="takeover-detail-container f-panel-content">
      {/* Header Info */}
      <div style={{ marginBottom: 32 }}>
        <div className="td-detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{ fontSize: 24, fontWeight: 600, margin: 0 }}>{takeover.signName}</h2>
            <button 
              className="f-button f-button-secondary" 
              style={{ padding: '4px 8px', height: 'auto' }} 
              onClick={() => {
                setIsEditingSign(true);
                fetchSigns();
              }}
            >
              <FaEdit /> Edit Sign
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ 
              padding: '4px 12px', 
              borderRadius: 2, 
              fontSize: 11, 
              fontWeight: 600, 
              background: takeover.paymentStatus === 'captured' ? '#dcfce7' : takeover.paymentStatus === 'authorized' ? '#fffbeb' : '#f3f2f1',
              color: takeover.paymentStatus === 'captured' ? '#107c10' : takeover.paymentStatus === 'authorized' ? '#92400e' : '#64748b',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              {takeover.paymentStatus === 'captured' && <FaCheckCircle size={10} />}
              {takeover.paymentStatus || 'No Payment'}
            </span>
            <span style={{ 
              padding: '4px 12px', 
              borderRadius: 2, 
              fontSize: 12, 
              fontWeight: 600, 
              background: 
                status === 'Approved' || status === 'Complete' ? '#dcfce7' : 
                status === 'Rejected' || status === 'Canceled' ? '#fee2e2' : 
                status === 'Pending' ? '#fffbeb' :
                status === 'Upcoming' ? '#e0f2fe' : 
                '#f3f2f1', 
              color: 
                status === 'Approved' || status === 'Complete' ? '#166534' : 
                status === 'Rejected' || status === 'Canceled' ? '#991b1b' : 
                status === 'Pending' ? '#92400e' :
                status === 'Upcoming' ? '#0369a1' : 
                '#605e5c',
              textTransform: 'uppercase'
            }}>
              {status || 'New'}
            </span>
          </div>
        </div>
        <p style={{ fontSize: 14, color: 'var(--ms-text-secondary)', margin: 0 }}>ID: {takeover.id}</p>
      </div>

      {/* Overview Card */}
      <div className="f-card">
        <h3 className="detail-section-label">Overview</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 14 }}><b>Address:</b> {takeover.address || '—'}</div>
          <div style={{ fontSize: 14 }}><b>DMA:</b> {takeover.dma || '—'}</div>
          <div style={{ fontSize: 14 }}><b>Resolution:</b> {takeover.signResolution || '—'}</div>
          {takeover.professionalPhotoOp && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ 
                fontSize: 14, 
                color: '#007bff', 
                fontWeight: 'bold',
                padding: '8px 12px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px',
                display: 'inline-block',
                width: 'fit-content'
              }}>
                ✓ Professional Photo Op Requested
              </div>
              <button
                onClick={handleCopyPhotoOpInstructions}
                style={{
                  padding: '8px 16px',
                  backgroundColor: copyPhotoOpSuccess ? '#28a745' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  whiteSpace: 'nowrap'
                }}
              >
                {copyPhotoOpSuccess ? '✓ Copied!' : '📋 Copy Instructions'}
              </button>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Activation Mode:</span>
            {takeover.autoActivateAtScheduledTime === true ? (
              <span style={{
                fontSize: 15,
                fontWeight: 800,
                padding: '5px 14px',
                borderRadius: 4,
                background: '#dcfce7',
                color: '#14532d',
                border: '2px solid #16a34a',
                textTransform: 'uppercase',
                letterSpacing: '0.08em'
              }}>⚡ AUTO ACTIVATE</span>
            ) : takeover.autoActivateAtScheduledTime === false ? (
              <span style={{
                fontSize: 15,
                fontWeight: 800,
                padding: '5px 14px',
                borderRadius: 4,
                background: '#fef3c7',
                color: '#78350f',
                border: '2px solid #d97706',
                textTransform: 'uppercase',
                letterSpacing: '0.08em'
              }}>✋ MANUAL</span>
            ) : (
              <span style={{ fontSize: 14, color: 'var(--ms-text-secondary)' }}>—</span>
            )}
          </div>
          
          {(() => {
            const revenue = Number(takeover.totalPrice || 0);
            const dspCost = isDspOrder ? Number(dspCampaign?.totalBudget ?? dspCampaign?.budget ?? 0) : 0;
            const profit = revenue - dspCost;
            const profitColor = profit > 0 ? '#166534' : profit < 0 ? '#991b1b' : '#374151';
            return (
              <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 2 }}>Revenue</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--ms-primary)' }}>{formatCurrency(revenue)}</div>
                </div>
                {isDspOrder && (
                  <>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 2 }}>DSP Cost</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: '#374151' }}>
                        {dspCampaignLoading ? '…' : formatCurrency(dspCost)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 2 }}>Est. Profit</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: profitColor }}>
                        {dspCampaignLoading ? '…' : formatCurrency(profit)}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {isDspOrder && (
            <div style={{ marginTop: 8, padding: 12, border: '1px solid var(--ms-border-light)', borderRadius: 4, background: '#f8fafc' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ms-text-secondary)', marginBottom: 8, textTransform: 'uppercase' }}>
                DSP Campaign
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                  <span style={{ color: 'var(--ms-text-secondary)', whiteSpace: 'nowrap' }}>Takeover ID:</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={dspCampaignId}>{dspCampaignId}</span>
                </div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  <div style={{ fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>
                    Advertiser URL (DSP submit & bidding)
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={dspAdvertiserUrlDraft}
                      onChange={(e) => setDspAdvertiserUrlDraft(e.target.value)}
                      placeholder="https://example.com or example.com"
                      disabled={dspCampaignLoading}
                      style={{
                        flex: '1 1 220px',
                        minWidth: 0,
                        padding: '6px 8px',
                        fontSize: 12,
                        borderRadius: 4,
                        border: '1px solid var(--ms-border-light)'
                      }}
                    />
                    <button
                      type="button"
                      className="f-button f-button-secondary"
                      onClick={saveDspAdvertiserUrl}
                      disabled={dspCampaignLoading || dspAdvertiserUrlSaving || !dspCampaign}
                      style={{ height: 32, fontSize: 12 }}
                      title="Writes clientWebsite (+ derived advertiser domain) to takeovers/ and active_dsp_campaigns/"
                    >
                      {dspAdvertiserUrlSaving ? (
                        <>
                          <FaSpinner className="fa-spin" style={{ marginRight: 6 }} />
                          Saving…
                        </>
                      ) : (
                        'Save URL'
                      )}
                    </button>
                  </div>
                  {!!dspAdvertiserUrlDraft.trim() && (
                    <div style={{ fontSize: 11, color: 'var(--ms-text-secondary)', marginTop: 6 }}>
                      Domain sent as <span style={{ fontFamily: 'monospace' }}>advertiser_domain</span>:{' '}
                      <span style={{ fontFamily: 'monospace' }}>{extractDomain(dspAdvertiserUrlDraft.trim()) || '—'}</span>
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 12, marginTop: 10 }}>
                  <div style={{ fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>
                    DSP Budget (budget & totalBudget)
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={dspBudgetDraft}
                      onChange={(e) => setDspBudgetDraft(e.target.value)}
                      placeholder="0.00"
                      disabled={dspCampaignLoading}
                      style={{
                        flex: '0 1 160px',
                        minWidth: 120,
                        padding: '6px 8px',
                        fontSize: 12,
                        borderRadius: 4,
                        border: '1px solid var(--ms-border-light)'
                      }}
                    />
                    <button
                      type="button"
                      className="f-button f-button-secondary"
                      onClick={saveDspBudget}
                      disabled={dspCampaignLoading || dspBudgetSaving || !dspCampaign}
                      style={{ height: 32, fontSize: 12 }}
                      title="Writes budget and totalBudget to takeovers/ and active_dsp_campaigns/"
                    >
                      {dspBudgetSaving ? (
                        <>
                          <FaSpinner className="fa-spin" style={{ marginRight: 6 }} />
                          Saving…
                        </>
                      ) : (
                        'Save Budget'
                      )}
                    </button>
                  </div>
                </div>
                {dspCampaignLoading && <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>Loading…</div>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button
                  className="f-button f-button-secondary"
                  onClick={handleCreateWarmupCampaign}
                  disabled={warmupLoading || dspCampaignLoading || !dspCampaign}
                  style={{ height: 32, fontSize: 12 }}
                  title="Warm-up budget = (totalBudget ÷ 15) × 2. Runs daily 00:00–11:59 for 3 days."
                >
                  {warmupLoading ? <><FaSpinner className="fa-spin" style={{ marginRight: 6 }} /> Creating…</> : 'Create Warm-Up Campaign'}
                </button>
                <button
                  className="f-button f-button-secondary"
                  onClick={openDspScheduleEditor}
                  disabled={dspCampaignLoading || !dspCampaign || dspScheduleLoading}
                  style={{ height: 32, fontSize: 12 }}
                  title="Edit DSP bidding schedule (writes to takeovers/ and active_dsp_campaigns/)"
                >
                  Edit DSP Schedule
                </button>
              </div>
            </div>
          )}
          
          {takeover.paymentTransactionId && (
            <div style={{ padding: '12px', background: 'var(--ms-bg)', borderRadius: 4, marginTop: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)' }}>
                <FaCreditCard /> PAYMENT DETAILS
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--ms-text-secondary)' }}>Transaction ID:</span>
                  <span style={{ fontFamily: 'monospace' }}>{takeover.paymentTransactionId}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--ms-text-secondary)' }}>Payment Type:</span>
                  <span>{takeover.paymentType === 'authorize_only' ? 'Authorized (Hold)' : takeover.paymentType || 'Standard'}</span>
                </div>
                {takeover.captureTransactionId && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: 'var(--ms-text-secondary)' }}>Capture ID:</span>
                    <span style={{ fontFamily: 'monospace' }}>{takeover.captureTransactionId}</span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                {takeover.paymentStatus === 'authorized' && (
                  <>
                    <button 
                      className="f-button" 
                      style={{ flex: 1, height: 32, fontSize: 12 }} 
                      onClick={() => setShowPaymentModal(true)}
                      disabled={paymentLoading}
                    >
                      Capture Funds
                    </button>
                    <button 
                      className="f-button f-button-secondary" 
                      style={{ flex: 1, height: 32, fontSize: 12, color: '#b91c1c' }} 
                      onClick={handleVoidTransaction}
                      disabled={paymentLoading}
                    >
                      Void
                    </button>
                  </>
                )}
                {takeover.paymentStatus === 'captured' && (
                  <button 
                    className="f-button f-button-secondary" 
                    style={{ flex: 1, height: 32, fontSize: 12, color: '#b91c1c' }} 
                    onClick={handleRefundPayment}
                    disabled={paymentLoading}
                  >
                    {paymentLoading ? <FaSpinner className="fa-spin" /> : 'Issue Refund'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Customer Info */}
      <div className="f-card">
        <h3 className="detail-section-label">Customer Information</h3>
        {userInfo ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--ms-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
                {(userInfo.name || userInfo.email || '?')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{userInfo.name || '—'}</div>
                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{userInfo.email}</div>
              </div>
            </div>
            {userInfo.phoneNumber && <div style={{ fontSize: 13 }}><FaPhone style={{ marginRight: 8, color: 'var(--ms-text-secondary)' }} /> {userInfo.phoneNumber}</div>}
          </div>
        ) : <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>Loading...</div>}
      </div>

      {/* Activation & Plays */}
      <div className="f-card">
        <h3 className="detail-section-label">Activation & Plays</h3>

        <div className={`td-plays-hero ${totalWins > 0 ? 'has-plays' : ''}`}>
          <div>
            <div className="td-plays-hero-number">
              {totalWinsLoading && !playsLoadedRef.current ? '—' : totalWins.toLocaleString()}
            </div>
            <div className="td-plays-hero-caption">
              {showDailyPlayFields && displayedTargetPlays > 0
                ? `of ${displayedTargetPlays.toLocaleString()} target plays`
                : `play${totalWins === 1 ? '' : 's'} so far`}
            </div>
          </div>
          <div className="td-plays-hero-meta">
            {flipTypeLabel && <div className="td-plays-hero-flip">{flipTypeLabel}</div>}
            {playSpend > 0 && <div>{formatCurrency(playSpend)} billed</div>}
            {lastPlayAt && <div>Last play {dayjs(lastPlayAt).format('MMM D, h:mm A')}</div>}
          </div>
        </div>

        {showDailyPlayFields && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <div style={{ minWidth: 110 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 2 }}>Target Plays</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{displayedTargetPlays > 0 ? displayedTargetPlays.toLocaleString() : '—'}</div>
            </div>
            <div style={{ minWidth: 110 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ms-text-secondary)', textTransform: 'uppercase', marginBottom: 2 }}>Plays / Hour</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{hasPlaysPerHour ? playsPerHour.toLocaleString() : '—'}</div>
            </div>
          </div>
        )}

        {isDspOrder && (
          <div className="td-live-bid-card">
            <div className="td-live-bid-card-header">
              <div>
                <div className="td-live-bid-kicker">
                  <span className={`td-live-bid-status-dot ${liveBidStats.source === 'live' && liveBidTotal > 0 ? 'is-live' : ''}`} />
                  {liveBidStats.source === 'archive' ? 'Last 24 hours' : 'Live bidding'}
                </div>
                <h4>Bid activity</h4>
              </div>
              {showLiveBidChart && liveBidTotal > 0 && (
                <span className="td-live-bid-live-pill">
                  {liveBidStats.source === 'live' ? 'Live' : 'Archive'}
                </span>
              )}
            </div>
            {showLiveBidChart && (liveBidTotal > 0 || hasBidChartData) ? (
              <>
                <div className="td-live-bid-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={liveBidPieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={70}
                        outerRadius={96}
                        paddingAngle={2}
                        stroke="none"
                        isAnimationActive={false}
                        cx="50%"
                        cy="50%"
                      >
                        {liveBidPieData.map((slice) => (
                          <Cell key={slice.key} fill={slice.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => {
                          const n = Number(value || 0);
                          if (!n) return null;
                          const pct = liveBidTotal > 0 ? Math.round((n / liveBidTotal) * 100) : 0;
                          return [`${n.toLocaleString()} (${pct}%)`, name];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="td-live-bid-center">
                    <div className="td-live-bid-center-label">Total bids</div>
                    <div className="td-live-bid-center-value">{liveBidTotal.toLocaleString()}</div>
                    <div className="td-live-bid-center-rate">Win rate {liveBidWinRate}%</div>
                  </div>
                </div>
                <div className="td-live-bid-chips">
                  {LIVE_BID_LABELS.map((slice) => {
                    const value = Number(liveBidStats[slice.key] || 0);
                    if (!value && slice.key !== 'playouts') return null;
                    const pct = liveBidTotal > 0 ? Math.round((value / liveBidTotal) * 100) : 0;
                    return (
                      <span key={slice.key} className="td-live-bid-chip">
                        <span className="td-live-bid-dot" style={{ background: slice.color }} />
                        {slice.name} {value.toLocaleString()} ({pct}%)
                      </span>
                    );
                  })}
                </div>
                {(liveBidStats.lossReasons.length > 0 || liveBidStats.noBidReasons.length > 0) && (
                  <div className="td-live-bid-reasons">
                    {liveBidStats.lossReasons.length > 0 && (
                      <div className="td-live-bid-reason-col">
                        <div className="td-live-bid-reason-title">Loss reasons</div>
                        {liveBidStats.lossReasons.map((reason) => (
                          <div key={reason.reasonCode} className="td-live-bid-reason-row">
                            <span>{reason.reasonDescription || reason.reasonCode}</span>
                            <strong>{reason.count}</strong>
                          </div>
                        ))}
                      </div>
                    )}
                    {liveBidStats.noBidReasons.length > 0 && (
                      <div className="td-live-bid-reason-col">
                        <div className="td-live-bid-reason-title">No-bid reasons</div>
                        {liveBidStats.noBidReasons.map((reason) => (
                          <div key={reason.reasonCode} className="td-live-bid-reason-row">
                            <span>{reason.reasonDescription || reason.reasonCode}</span>
                            <strong>{reason.count}</strong>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="td-live-bid-empty">
                {isActivated || dspCampaign?.isActivated
                  ? 'Waiting for live bid activity on this takeover…'
                  : 'Activate the takeover to see live bid status.'}
              </div>
            )}
          </div>
        )}

        {/* Activation status / button */}
        {isActivated ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            borderRadius: 6,
            background: '#dcfce7',
            border: '1px solid #16a34a',
            marginBottom: 12
          }}>
            <FaCheckCircle size={14} color="#16a34a" />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Takeover Activated
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {takeover?.dailyTakeover && (
              <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>
                Daily takeover — normally activates automatically at scheduled time. You can still activate it now.
              </div>
            )}
            <button
              className="f-button"
              style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8 }}
              onClick={performActivation}
              disabled={activationLoading}
            >
              {activationLoading
                ? <><FaSpinner className="fa-spin" style={{ marginRight: 6 }} /> Activating…</>
                : <><FaBolt style={{ marginRight: 6 }} /> Activate Takeover</>}
            </button>
            {activationSuccess && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#166534', fontSize: 13 }}>
                <FaCheckCircle /> Activation successful
              </div>
            )}
            {activationError && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#b91c1c', fontSize: 13, padding: '8px 12px', background: '#fde7e9', borderRadius: 4 }}>
                <FaTimesCircle /> {activationError}
              </div>
            )}
          </div>
        )}

      </div>

      {/* Schedule */}
      <div className="f-card">
        <h3 className="detail-section-label">Schedule</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <input type="date" className="f-input" value={editDate} onChange={e => setEditDate(e.target.value)} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaClock size={11} /> Hours
              {editSelectedHours.length > 0 && (
                <span style={{ fontWeight: 500 }}>
                  · {editSelectedHours.length} selected
                  {estimatedPlays != null ? ` · ~${estimatedPlays.toLocaleString()} plays` : ''}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {Array.from({ length: 24 }, (_, hour) => {
                const selected = editSelectedHours.includes(hour);
                return (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => toggleSelectedHour(hour)}
                    style={{
                      height: 32,
                      minWidth: 72,
                      padding: '0 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: selected ? '1px solid #0b7fd4' : '1px solid var(--ms-border-light)',
                      background: selected ? '#e0f2fe' : '#fff',
                      color: selected ? '#0369a1' : 'var(--ms-text-secondary)',
                    }}
                  >
                    {formatHourLabel(hour)}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {editTimeSlots.map((slot, i) => (
              <div key={`${slot}-${i}`} className="time-slot-chip">
                {slot}
                <button type="button" onClick={() => handleRemoveTimeSlot(i)} className="time-slot-remove">&times;</button>
              </div>
            ))}
            <button type="button" className="f-button f-button-secondary" style={{ height: 24, fontSize: 11 }} onClick={handleAddTimeSlot}>+ Add</button>
          </div>
          <button className="f-button" onClick={handleSaveDateTime} disabled={loading}>
            {loading ? <><FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Saving…</> : 'Update Schedule'}
          </button>
        </div>
      </div>

      {creativeInfo && (
        <div className="f-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
            <h3 className="detail-section-label" style={{ margin: 0 }}>Submitted Asset</h3>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {creativeInfo.customDesign && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#e0f2fe', color: '#0369a1', textTransform: 'uppercase' }}>
                  Custom Design
                </span>
              )}
              {creativeInfo.creativeStatus && (
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#fffbeb', color: '#92400e', textTransform: 'uppercase' }}>
                  {creativeInfo.creativeStatus}
                </span>
              )}
            </div>
          </div>

          {creativeInfo.brief && (
            <div style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap', background: 'var(--ms-bg)', borderRadius: 6, padding: 12, marginBottom: 12 }}>
              {creativeInfo.brief}
            </div>
          )}
          {creativeInfo.extraBrief && (
            <div style={{ fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap', background: 'var(--ms-bg)', borderRadius: 6, padding: 12, marginBottom: 12 }}>
              {creativeInfo.extraBrief}
            </div>
          )}

          {creativeInfo.uniqueUrls.length > 0 ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', fontWeight: 600 }}>
                  {creativeInfo.uniqueUrls.length} image{creativeInfo.uniqueUrls.length === 1 ? '' : 's'}
                </div>
                <button
                  type="button"
                  className="f-button f-button-secondary"
                  onClick={handleDownloadSubmittedCreatives}
                  disabled={creativeDownloadLoading}
                  style={{ height: 28, fontSize: 11, padding: '0 10px' }}
                >
                  {creativeDownloadLoading ? (
                    <><FaSpinner className="fa-spin" style={{ marginRight: 6 }} /> {creativeDownloadProgress || 'Downloading…'}</>
                  ) : (
                    <><FaFileDownload style={{ marginRight: 6 }} /> Download All</>
                  )}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
                {creativeInfo.uniqueUrls.map((url, index) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setPreviewCreativeUrl(url)}
                    title={`Open image ${index + 1}`}
                    style={{
                      display: 'block',
                      height: 72,
                      padding: 0,
                      borderRadius: 6,
                      overflow: 'hidden',
                      border: '1px solid var(--ms-border-light)',
                      background: '#f3f2f1',
                      cursor: 'pointer',
                    }}
                  >
                    {isVideo(url) ? (
                      <video src={url} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <img src={url} alt={`Submitted asset ${index + 1}`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    )}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>No submitted asset images.</div>
          )}
        </div>
      )}

      {/* Creative */}
      <div className="f-card">
        <h3 className="detail-section-label">Creative</h3>
        {isDspOrder ? (
          dspCampaignLoading ? (
            <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>Loading DSP campaign creatives…</div>
          ) : !dspCampaign ? (
            <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>
              No DSP takeover doc found in <code style={{ fontFamily: 'monospace' }}>takeovers/{dspCampaignId || '...'}</code>
            </div>
          ) : (
            (() => {
              const creativesArr = Array.isArray(dspCampaign?.creatives) ? dspCampaign.creatives : [];
              const assigned = dspCampaign?.assignedCreatives && typeof dspCampaign.assignedCreatives === 'object'
                ? dspCampaign.assignedCreatives
                : null;
              const assignedArr = assigned
                ? Object.values(assigned).flatMap(v => Array.isArray(v) ? v : [])
                : [];
              const creativeList = creativesArr.length > 0 ? creativesArr : assignedArr;
              if (creativeList.length === 0) {
                return <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>No DSP creatives on this campaign.</div>;
              }
              return creativeList.map((creative, idx) => {
                const creativeDocId = creative?.id || creative?.creative_id || creative?.creativeId || `creative_${idx}`;
                const meta = dspCreativeMetaById[creativeDocId] || {};
                const dspCreativeId = meta.dspCreativeId || '';
                const statusObj = dspCreativeId ? dspCreativeStatusByDspId[dspCreativeId] : null;
                const statusLabel = (statusObj?.label || meta.moderationStatus || 'pending');
                // Re-add only after THIS takeover has been submitted to the publisher.
                // Do not use order status (new takeovers are often already "Pending")
                // or a leftover library dspCreativeId from another campaign.
                const submittedForThisTakeover =
                  String(meta.dspCampaignId || '') === String(dspCampaignId) &&
                  !!meta.dspSubmittedAt;
                const isReadd = submittedForThisTakeover;
                const statusColor =
                  statusLabel === 'approved' ? { bg: '#dcfce7', text: '#166534' } :
                  statusLabel === 'rejected' ? { bg: '#fee2e2', text: '#991b1b' } :
                  statusLabel === 'partial' ? { bg: '#e0f2fe', text: '#0369a1' } :
                  { bg: '#fffbeb', text: '#92400e' };
                const previewUrl = creative?.croppedImageUrl || creative?.croppedUrl || creative?.originalImageUrl;
                const normalizedKey = getNormalizedKeyForCreative(creative);
                const selectedIabCode = iabCategoryByFormat?.[normalizedKey] || '';

                return (
                  <div key={creativeDocId} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                    {isVideo(previewUrl) ? (
                      <video
                        src={previewUrl}
                        controls
                        playsInline
                        autoPlay
                        muted
                        loop
                        style={{ width: '100%', borderRadius: 2, border: '1px solid var(--ms-border-light)' }}
                      />
                    ) : (
                      <img src={previewUrl} style={{ width: '100%', borderRadius: 2, border: '1px solid var(--ms-border-light)' }} alt="" />
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ fontSize: 12 }}>
                        <b>Creative ID:</b> <span style={{ fontFamily: 'monospace' }}>{creativeDocId}</span>
                        {dspCreativeId && (
                          <span style={{ marginLeft: 10 }}>
                            <b>DSP ID:</b> <span style={{ fontFamily: 'monospace' }}>{dspCreativeId}</span>
                          </span>
                        )}
                      </div>
                      <span style={{ padding: '2px 8px', borderRadius: 2, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', background: statusColor.bg, color: statusColor.text }}>
                        {statusLabel}
                      </span>
                    </div>

                    {/* IAB Category (required for first-time DSP creative submissions) */}
                    {!isReadd && <div style={{ border: '1px solid var(--ms-border-light)', borderRadius: 4, padding: 10, background: '#f8fafc' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ms-text-secondary)', marginBottom: 6, textTransform: 'uppercase' }}>
                        IAB Category
                      </div>
                      <button
                        className="f-button f-button-secondary"
                        style={{ height: 30, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                        onClick={() => openIabPicker({ normalizedKey })}
                        disabled={approveLoading || dspSubmittingCreativeId === creativeDocId}
                        title="Required before submitting to publisher"
                      >
                        <span style={{ textAlign: 'left' }}>
                          {selectedIabCode ? (
                            <span>
                              <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{selectedIabCode}</span>
                              <span style={{ marginLeft: 8, color: 'var(--ms-text-secondary)' }}>— {getIabName(selectedIabCode)}</span>
                            </span>
                          ) : (
                            <span style={{ color: 'var(--ms-text-secondary)' }}>Search & select category…</span>
                          )}
                        </span>
                        <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, color: '#92400e' }}>Required</span>
                      </button>
                      {iabCategoriesLoading && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ms-text-secondary)' }}>Loading categories…</div>}
                      {!iabCategoriesLoading && iabCategoriesError && <div style={{ marginTop: 6, fontSize: 12, color: '#b91c1c' }}>{iabCategoriesError}</div>}
                    </div>}

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className={isReadd ? 'f-button f-button-secondary' : 'f-button'}
                        style={isReadd ? { color: '#0369a1', borderColor: '#0369a1' } : {}}
                        onClick={() => handleSubmitDspCreative({ ...creative, __isReadd: isReadd })}
                        disabled={approveLoading || dspSubmittingCreativeId === creativeDocId}
                        title={isReadd ? 'Already submitted — re-mirrors this creative into takeovers/ and active_dsp_campaigns without re-submitting to the publisher' : 'Submit creative to publisher for approval'}
                      >
                        {dspSubmittingCreativeId === creativeDocId
                          ? <><FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> {isReadd ? 'Re-adding…' : 'Submitting…'}</>
                          : isReadd
                            ? 'Re-add to Active Campaigns'
                            : 'Submit Creative to Publisher'}
                      </button>
                      {canSwapDspCreatives && (
                        <button
                          className="f-button f-button-secondary"
                          onClick={() => handleOpenSwapCreative(creativeDocId)}
                          disabled={approveLoading || swapCreativeLoading}
                          title="Swap the creative before submitting to the vendor"
                        >
                          Swap Creative
                        </button>
                      )}
                      {dspCreativeId && (
                        <button
                          className="f-button f-button-secondary"
                          onClick={async () => {
                            try {
                              const statusObj2 = await fetchDspCreativeStatus(dspCreativeId);
                              setDspCreativeStatusByDspId(prev => ({ ...prev, [dspCreativeId]: statusObj2 }));
                              const sign0b = dspCampaign?.signs?.[0] || {};
                              const sellerIdGuess =
                                sign0b?.seller_id ||
                                sign0b?.sellerId ||
                                sign0b?.publisher_id ||
                                sign0b?.publisherId ||
                                sign0b?.publisher ||
                                sign0b?.SellerID ||
                                sign0b?.SellerId ||
                                sign0b?.['Seller ID'] ||
                                sign0b?.['Seller Id'] ||
                                '';
                              await persistDspCreativeStatusToFirestore({ creativeDocId, dspCreativeId, sellerId: sellerIdGuess, statusObj: statusObj2 });
                              setDspCreativeMetaById(prev => ({ ...prev, [creativeDocId]: { ...(prev[creativeDocId] || {}), moderationStatus: statusObj2?.label || 'pending' } }));
                            } catch (e) {
                              alert(`Status refresh failed: ${e.message || 'Unknown error'}`);
                            }
                          }}
                        >
                          Refresh DSP Status
                        </button>
                      )}
                    </div>
                  </div>
                );
              });
            })()
          )
        ) : (
          takeover.creativeDetails ? Object.entries(takeover.creativeDetails).map(([key, creative]) => (
            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {isVideo(creative.croppedUrl) ? (
                <video 
                  src={creative.croppedUrl} 
                  controls 
                  playsInline
                  autoPlay
                  muted
                  loop
                  style={{ width: '100%', borderRadius: 2, border: '1px solid var(--ms-border-light)' }} 
                />
              ) : (
                <img src={creative.croppedUrl} style={{ width: '100%', borderRadius: 2, border: '1px solid var(--ms-border-light)' }} alt="" />
              )}
              <div style={{ fontSize: 12 }}><b>Status:</b> {creative.status}</div>
              {creative.status !== 'Approved' && (
                <button className="f-button" onClick={() => handleApproveCreative(key)} disabled={approveLoading}>Approve Creative</button>
              )}
            </div>
          )) : <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>No creatives</div>
        )}
      </div>

      {/* Actions */}
      <div style={{ marginTop: 24 }}>
        <h3 className="detail-section-label">Order Actions</h3>
        <div className="td-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <button className="f-button f-button-secondary" onClick={openPhotoOpModal}>
            Photo Op link
          </button>
          <button className="f-button" onClick={() => handleUpdateStatus('Complete')}>Complete</button>
          <button className="f-button f-button-secondary" onClick={() => handleUpdateStatus('Approved')}>Approve</button>
          <button className="f-button f-button-secondary" onClick={() => handleUpdateStatus('Pending')}>Set Pending</button>
          <button className="f-button f-button-secondary" onClick={() => handleUpdateStatus('Upcoming')}>Set Upcoming</button>
          <button className="f-button f-button-secondary" style={{ color: '#d13438' }} onClick={() => handleUpdateStatus('Rejected')}>Reject Order</button>
          <button className="f-button f-button-secondary" style={{ color: '#64748b' }} onClick={() => handleUpdateStatus('Canceled')}>Cancel Order</button>
        </div>
      </div>

      {showPaymentModal && (
        <div className="td-modal-overlay" style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div className="f-card" style={{ maxWidth: 400, width: '100%', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Capture Payment</h3>
              <button onClick={() => setShowPaymentModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}><FaTimes /></button>
            </div>
            
            <p style={{ fontSize: 14, marginBottom: 20 }}>
              This order has an authorized payment. You must capture the payment to mark the order as complete.
            </p>
            
            <div style={{ background: 'var(--ms-bg)', padding: 16, borderRadius: 4, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 14 }}>
                <span>Transaction ID:</span>
                <span style={{ fontWeight: 600, fontFamily: 'monospace' }}>{takeover.paymentTransactionId}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 600 }}>
                <span>Amount:</span>
                <span style={{ color: 'var(--ms-primary)' }}>{formatCurrency(takeover.paymentAmount || takeover.totalPrice)}</span>
              </div>
            </div>

            {paymentError && (
              <div style={{ color: '#d13438', fontSize: 13, marginBottom: 20, padding: 10, background: '#fde7e9', borderRadius: 4 }}>
                <FaTimesCircle style={{ marginRight: 8 }} /> {paymentError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button 
                className="f-button" 
                style={{ flex: 2 }} 
                onClick={handleCapturePayment} 
                disabled={paymentLoading}
              >
                {paymentLoading ? <><FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Processing...</> : 'Capture Payment'}
              </button>
              <button 
                className="f-button f-button-secondary" 
                style={{ flex: 1 }} 
                onClick={() => setShowPaymentModal(false)}
                disabled={paymentLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {swapCreativeOpen && (
        <div className="td-modal-overlay" style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div className="f-card" style={{ maxWidth: 520, width: '100%', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Swap DSP Creative</h3>
              <button
                onClick={() => { if (!swapCreativeLoading) setSwapCreativeOpen(false); }}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)', marginBottom: 12 }}>
              Allowed only before warm-up creation and before vendor submission/approval.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 13 }}>
                <b>Old Creative ID:</b> <span style={{ fontFamily: 'monospace' }}>{swapOldCreativeId}</span>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>
                  New Creative Doc ID (from <span style={{ fontFamily: 'monospace' }}>creatives/&lt;id&gt;</span>)
                </div>
                <input
                  type="text"
                  className="f-input"
                  style={{ fontFamily: 'monospace' }}
                  placeholder="e.g. creative_abc123"
                  value={swapNewCreativeId}
                  onChange={(e) => setSwapNewCreativeId(e.target.value)}
                  disabled={swapCreativeLoading}
                />
              </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="f-button f-button-secondary"
                    onClick={openCreateCreative}
                    disabled={swapCreativeLoading || approveLoading}
                    style={{ height: 30, fontSize: 12 }}
                    title="Upload a new creative file and create a creatives/<id> doc"
                  >
                    Upload New Creative
                  </button>
                </div>

              {swapCreativeError && (
                <div style={{ color: '#d13438', fontSize: 13, padding: 10, background: '#fde7e9', borderRadius: 4 }}>
                  <FaTimesCircle style={{ marginRight: 8 }} /> {swapCreativeError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                <button
                  className="f-button"
                  onClick={handleConfirmSwapCreative}
                  disabled={swapCreativeLoading}
                  style={{ flex: 2 }}
                >
                  {swapCreativeLoading ? <><FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Swapping…</> : 'Swap Creative'}
                </button>
                <button
                  className="f-button f-button-secondary"
                  onClick={() => setSwapCreativeOpen(false)}
                  disabled={swapCreativeLoading}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {createCreativeOpen && (
        <div className="td-modal-overlay" style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: 20
        }}>
          <div className="f-card" style={{ maxWidth: 560, width: '100%', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Upload / Create Creative</h3>
              <button
                onClick={() => { if (!createCreativeLoading) setCreateCreativeOpen(false); }}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>
                This uploads to Firebase Storage and creates a new <span style={{ fontFamily: 'monospace' }}>creatives/&lt;id&gt;</span> doc. After upload, the new id is auto-filled in the Swap modal.
              </div>

              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>File</div>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => setCreateCreativeFile(e.target.files?.[0] || null)}
                  disabled={createCreativeLoading}
                />
              </div>

              <div className="td-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>Name (optional)</div>
                  <input
                    type="text"
                    className="f-input"
                    placeholder="e.g. takeover-creative"
                    value={createCreativeName}
                    onChange={(e) => setCreateCreativeName(e.target.value)}
                    disabled={createCreativeLoading}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>Dimensions (optional)</div>
                  <input
                    type="text"
                    className="f-input"
                    placeholder="e.g. 1080x1920"
                    value={createCreativeDimensions}
                    onChange={(e) => setCreateCreativeDimensions(e.target.value)}
                    disabled={createCreativeLoading}
                  />
                </div>
              </div>

              {createCreativeError && (
                <div style={{ color: '#d13438', fontSize: 13, padding: 10, background: '#fde7e9', borderRadius: 4 }}>
                  <FaTimesCircle style={{ marginRight: 8 }} /> {createCreativeError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                <button
                  className="f-button"
                  onClick={handleCreateAndUploadCreative}
                  disabled={createCreativeLoading}
                  style={{ flex: 2 }}
                >
                  {createCreativeLoading ? <><FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Uploading…</> : 'Upload & Create'}
                </button>
                <button
                  className="f-button f-button-secondary"
                  onClick={() => setCreateCreativeOpen(false)}
                  disabled={createCreativeLoading}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showIabPickerModal && (
        <div className="td-modal-overlay" style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1200,
          padding: 20
        }}>
          <div className="f-card" style={{ maxWidth: 640, width: '100%', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Select IAB Category</h3>
              <button
                onClick={() => {
                  setShowIabPickerModal(false);
                  setPendingIabNormalizedKey('');
                  setPendingIabCode('');
                  setIabSearchTerm('');
                  setPendingDspSubmitCreative(null);
                }}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)', marginBottom: 10 }}>
              Required for DSP creative submissions. Search by code or name — all matches are shown while searching.
            </div>

            <div style={{ position: 'relative', marginBottom: 12 }}>
              <FaSearch style={{ position: 'absolute', left: 12, top: 12, color: 'var(--ms-text-secondary)' }} />
              <input
                ref={iabSearchInputRef}
                type="text"
                className="f-input"
                style={{ paddingLeft: 36 }}
                placeholder="Search IAB categories by code or name…"
                value={iabSearchTerm}
                onChange={(e) => setIabSearchTerm(e.target.value)}
                disabled={iabCategoriesLoading}
              />
            </div>

            <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--ms-border-light)', borderRadius: 4 }}>
              {iabCategoriesLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240, flexDirection: 'column', gap: 12 }}>
                  <FaSpinner className="fa-spin" size={24} />
                  <span>Loading categories...</span>
                </div>
              ) : getFilteredIabCategories().length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--ms-text-secondary)', fontSize: 13 }}>
                  {String(iabSearchTerm || '').trim()
                    ? 'No categories match your search.'
                    : 'No categories available.'}
                </div>
              ) : (
                getFilteredIabCategories().map((c) => {
                    const code = String(c?.code || '').trim();
                    const name = String(c?.name || '').trim();
                    const selected = String(pendingIabCode || '').trim() === code;
                    return (
                      <div
                        key={code || name}
                        onClick={() => setPendingIabCode(code)}
                        style={{
                          padding: '10px 14px',
                          borderBottom: '1px solid var(--ms-border-light)',
                          cursor: 'pointer',
                          background: selected ? '#e0f2fe' : 'transparent'
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 700 }}>
                          <span style={{ fontFamily: 'monospace' }}>{code}</span>
                          <span style={{ marginLeft: 8, fontWeight: 500, color: 'var(--ms-text-secondary)' }}>{name}</span>
                        </div>
                      </div>
                    );
                  })
              )}
            </div>

            {iabCategoriesError && (
              <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13 }}>
                {iabCategoriesError}
              </div>
            )}

            {!iabCategoriesLoading && !String(iabSearchTerm || '').trim() && (iabCategories?.length || 0) > 200 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ms-text-secondary)' }}>
                Showing first 200 categories — type to search the full list.
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button className="f-button" style={{ flex: 2 }} onClick={confirmIabAndContinue} disabled={iabCategoriesLoading}>
                Confirm
              </button>
              <button
                className="f-button f-button-secondary"
                style={{ flex: 1 }}
                onClick={() => {
                  setShowIabPickerModal(false);
                  setPendingIabNormalizedKey('');
                  setPendingIabCode('');
                  setIabSearchTerm('');
                  setPendingDspSubmitCreative(null);
                }}
                disabled={iabCategoriesLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {existsChoiceOpen && (
        <div className="td-modal-overlay" style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300,
          padding: 20
        }}>
          <div className="f-card" style={{ maxWidth: 560, width: '100%', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{existsChoiceTitle}</h3>
              <button
                onClick={() => resolveExistsChoice('cancel')}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
                disabled={existsChoiceLoading}
              >
                <FaTimes />
              </button>
            </div>

            {existsChoiceBody && (
              <pre style={{
                margin: 0,
                padding: 12,
                background: 'var(--ms-bg)',
                borderRadius: 4,
                border: '1px solid var(--ms-border-light)',
                fontSize: 12,
                whiteSpace: 'pre-wrap'
              }}>
                {existsChoiceBody}
              </pre>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                className="f-button"
                onClick={() => resolveExistsChoice('link')}
                disabled={existsChoiceLoading}
                style={{ flex: 1 }}
                title="Proceed without re-submitting; reuse the existing publisher creative"
              >
                Link Existing
              </button>
              <button
                className="f-button f-button-secondary"
                onClick={() => resolveExistsChoice('resubmit')}
                disabled={existsChoiceLoading}
                style={{ flex: 1 }}
                title="Create a new DSP creative id and submit again"
              >
                Submit Anyway
              </button>
              <button
                className="f-button f-button-secondary"
                onClick={() => resolveExistsChoice('cancel')}
                disabled={existsChoiceLoading}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {dspScheduleOpen && (
        <div className="td-modal-overlay" style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1400,
          padding: 20
        }}>
          <div className="f-card" style={{ maxWidth: 640, width: '100%', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>DSP Bidding Schedule</h3>
              <button
                onClick={() => { if (!dspScheduleLoading) setDspScheduleOpen(false); }}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
              >
                <FaTimes />
              </button>
            </div>

            <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)', marginBottom: 12 }}>
              This updates <span style={{ fontFamily: 'monospace' }}>takeovers/{dspCampaignId}</span> and <span style={{ fontFamily: 'monospace' }}>active_dsp_campaigns/{dspCampaignId}</span> so bidding sees the new <b>activeHours</b>/<b>activeDays</b>.
            </div>

            <div className="td-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>Start (local time)</div>
                <input type="datetime-local" className="f-input" value={dspEditStartDateTime} onChange={(e) => setDspEditStartDateTime(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>End (local time)</div>
                <input type="datetime-local" className="f-input" value={dspEditEndDateTime} onChange={(e) => setDspEditEndDateTime(e.target.value)} />
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 8 }}>Active Days</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label, day) => {
                  const checked = (dspEditDays || []).includes(day);
                  return (
                    <label key={day} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setDspEditDays((prev) => {
                            const s = new Set((prev || []).map(Number));
                            if (on) s.add(day);
                            else s.delete(day);
                            return [...s];
                          });
                        }}
                        disabled={dspScheduleLoading}
                      />
                      {label}
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="td-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>Daily Start Time</div>
                <input type="time" className="f-input" value={dspEditStartTime} onChange={(e) => setDspEditStartTime(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ms-text-secondary)', marginBottom: 6 }}>Daily End Time</div>
                <input type="time" className="f-input" value={dspEditEndTime} onChange={(e) => setDspEditEndTime(e.target.value)} />
              </div>
            </div>

            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={dspApplyToWarmup}
                onChange={(e) => setDspApplyToWarmup(e.target.checked)}
                disabled={dspScheduleLoading}
              />
              <div style={{ fontSize: 13, color: 'var(--ms-text-secondary)' }}>
                Also apply to warm-up (<span style={{ fontFamily: 'monospace' }}>warmup-{dspCampaignId}</span>) if it exists
              </div>
            </div>

            {dspScheduleError && (
              <div style={{ marginTop: 12, color: '#b91c1c', fontSize: 13, padding: 10, background: '#fde7e9', borderRadius: 4 }}>
                <FaTimesCircle style={{ marginRight: 8 }} /> {dspScheduleError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button className="f-button" style={{ flex: 2 }} onClick={saveDspSchedule} disabled={dspScheduleLoading}>
                {dspScheduleLoading ? <><FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Saving…</> : 'Save Schedule'}
              </button>
              <button className="f-button f-button-secondary" style={{ flex: 1 }} onClick={() => setDspScheduleOpen(false)} disabled={dspScheduleLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditingSign && (
        <div className="td-modal-overlay" style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div className="f-card" style={{ maxWidth: 600, width: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Change Sign</h3>
              <button onClick={() => setIsEditingSign(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}><FaTimes /></button>
            </div>

            <div style={{ position: 'relative', marginBottom: 16 }}>
              <FaSearch style={{ position: 'absolute', left: 12, top: 12, color: 'var(--ms-text-secondary)' }} />
              <input 
                type="text" 
                className="f-input" 
                style={{ paddingLeft: 36 }} 
                placeholder="Search signs by name or address..." 
                value={signSearchTerm}
                onChange={(e) => setSignSearchTerm(e.target.value)}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', minHeight: 300, border: '1px solid var(--ms-border-light)', borderRadius: 4 }}>
              {signSearchLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 12 }}>
                  <FaSpinner className="fa-spin" size={24} />
                  <span>Loading signs...</span>
                </div>
              ) : (
                allSigns
                  .filter(s => 
                    !signSearchTerm || 
                    (s.asset_name || s.name || '').toLowerCase().includes(signSearchTerm.toLowerCase()) ||
                    (s.address || '').toLowerCase().includes(signSearchTerm.toLowerCase())
                  )
                  .slice(0, 50)
                  .map(sign => (
                    <div 
                      key={sign.adunit_id || sign.id} 
                      className="sign-search-result"
                      style={{ 
                        padding: '12px 16px', 
                        borderBottom: '1px solid var(--ms-border-light)', 
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                      }}
                      onClick={() => handleUpdateSign(sign)}
                    >
                      {sign.asset_image_url || sign.img ? (
                        <img src={sign.asset_image_url || sign.img} style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 2 }} alt="" />
                      ) : (
                        <div style={{ width: 60, height: 40, background: '#f3f2f1', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
                          <FaMapMarkerAlt color="#64748b" />
                        </div>
                      )}
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{sign.asset_name || sign.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>{sign.address || `${sign.city}, ${sign.state}`}</div>
                      </div>
                    </div>
                  ))
              )}
              {!signSearchLoading && allSigns.length > 0 && allSigns.filter(s => 
                !signSearchTerm || 
                (s.asset_name || s.name || '').toLowerCase().includes(signSearchTerm.toLowerCase()) ||
                (s.address || '').toLowerCase().includes(signSearchTerm.toLowerCase())
              ).length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--ms-text-secondary)' }}>
                  No signs found matching your search.
                </div>
              )}
            </div>

            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
              <button className="f-button f-button-secondary" onClick={() => setIsEditingSign(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showPhotoOpModal && (
        <div className="td-modal-overlay" style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: 20
        }}>
          <div className="f-card" style={{ maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', margin: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Photo Op link</h3>
                <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--ms-text-secondary)' }}>
                  Create a photographer activation link with PIN for this takeover sign
                </p>
              </div>
              <button onClick={() => setShowPhotoOpModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>
                <FaTimes />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Photo op name</label>
                <input
                  type="text"
                  className="f-input"
                  maxLength={120}
                  value={photoOpName}
                  onChange={(e) => {
                    setPhotoOpNameEdited(true);
                    setPhotoOpName(e.target.value);
                  }}
                  placeholder={getPhotoOpDefaultName()}
                />
              </div>

              <div style={{ fontSize: 13, padding: '10px 12px', background: '#f8fafc', borderRadius: 4, border: '1px solid var(--ms-border-light)' }}>
                <div style={{ fontWeight: 600 }}>{takeover.signName || 'Sign'}</div>
                <div style={{ color: 'var(--ms-text-secondary)', marginTop: 4 }}>{takeover.address || '—'}</div>
                <div style={{ color: 'var(--ms-text-secondary)', marginTop: 4, fontFamily: 'monospace', fontSize: 11 }}>
                  Sign key: {getTakeoverPhotoOpSignKey() || 'missing'}
                </div>
              </div>

              <div className="td-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Window start</label>
                  <input type="datetime-local" className="f-input" value={photoOpWindowStart} onChange={(e) => setPhotoOpWindowStart(e.target.value)} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Window end</label>
                  <input type="datetime-local" className="f-input" value={photoOpWindowEnd} onChange={(e) => setPhotoOpWindowEnd(e.target.value)} />
                </div>
              </div>

              <div className="td-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Total plays</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className="f-input"
                    value={photoOpTotalPlays}
                    onChange={(e) => setPhotoOpTotalPlays(Number(e.target.value) || PHOTO_OP_DEFAULT_PLAYS)}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Photo op budget ($)</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    className="f-input"
                    value={photoOpBudget}
                    onChange={(e) => {
                      setPhotoOpBudgetEdited(true);
                      setPhotoOpBudget(Number(e.target.value) || 0);
                    }}
                  />
                </div>
              </div>

              <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)', padding: '10px 12px', background: '#f8fafc', borderRadius: 4 }}>
                Creates a separate photo op campaign with <strong>{photoOpTotalPlays || PHOTO_OP_DEFAULT_PLAYS} plays</strong> and{' '}
                <strong>{formatCurrency(photoOpBudget || 0)}</strong> budget.
                {!photoOpBudgetEdited && Number(takeover?.totalPrice || 0) > 0 && (
                  <button
                    type="button"
                    onClick={() => setPhotoOpBudget(Number(takeover.totalPrice))}
                    style={{ marginLeft: 8, background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Use order price ({formatCurrency(takeover.totalPrice)})
                  </button>
                )}
              </div>

              {photoOpCreatedLinks.length > 0 && (
                <div style={{ border: '1px solid #dbeafe', background: '#eff6ff', borderRadius: 6, padding: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Generated links (this session)</div>
                  {photoOpCreatedLinks.map((entry) => (
                    <div key={entry.sessionId} style={{ background: '#fff', border: '1px solid #dbeafe', borderRadius: 4, padding: 10, marginBottom: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{getPhotoOpDisplayLabel(entry)}</div>
                        <button
                          type="button"
                          className="f-button f-button-secondary"
                          style={{ height: 28, fontSize: 11, color: '#dc2626' }}
                          disabled={photoOpDeletingSessionId === entry.sessionId}
                          onClick={() => deletePhotoOpSession(entry.sessionId, getPhotoOpDisplayLabel(entry))}
                        >
                          {photoOpDeletingSessionId === entry.sessionId ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <input type="text" className="f-input" readOnly value={entry.linkUrl} style={{ fontSize: 11 }} />
                        <button type="button" className="f-button f-button-secondary" style={{ height: 32, fontSize: 11 }} onClick={() => copyTextToClipboard(entry.linkUrl, 'Link copied')}>
                          Copy
                        </button>
                      </div>
                      <div style={{ fontSize: 12 }}>
                        PIN: <strong style={{ letterSpacing: '0.15em' }}>{entry.pin}</strong>
                        <button type="button" onClick={() => copyTextToClipboard(entry.pin, 'PIN copied')} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}>
                          Copy PIN
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {photoOpSessionsLoading ? (
                <div style={{ fontSize: 12, color: 'var(--ms-text-secondary)' }}>Loading existing photo op sessions…</div>
              ) : photoOpSessionList.length > 0 ? (
                <div style={{ border: '1px solid var(--ms-border-light)', borderRadius: 6, padding: 12, background: '#f8fafc' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>All photo op sessions</div>
                  {photoOpSessionList.map((sess) => (
                    <div key={sess.sessionId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', background: '#fff', border: '1px solid var(--ms-border-light)', borderRadius: 4, padding: '8px 10px', marginBottom: 6, fontSize: 12 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{getPhotoOpDisplayLabel(sess)}</div>
                        <div style={{ color: 'var(--ms-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sess.signLabel || sess.sessionId}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{ padding: '2px 8px', borderRadius: 999, fontWeight: 600, background: sess.isActivated ? '#dcfce7' : '#f3f4f6', color: sess.isActivated ? '#166534' : '#374151' }}>
                          {sess.isActivated ? 'Live' : 'Awaiting check-in'}
                        </span>
                        {!sess.isActivated && (
                          <button
                            type="button"
                            className="f-button f-button-secondary"
                            style={{ height: 28, fontSize: 11, color: '#dc2626' }}
                            disabled={photoOpDeletingSessionId === sess.sessionId}
                            onClick={() => deletePhotoOpSession(sess.sessionId, getPhotoOpDisplayLabel(sess))}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {photoOpError && (
                <div style={{ color: '#d13438', fontSize: 13, padding: 10, background: '#fde7e9', borderRadius: 4 }}>
                  <FaTimesCircle style={{ marginRight: 8 }} /> {photoOpError}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
              <button type="button" className="f-button f-button-secondary" style={{ flex: 1 }} onClick={() => setShowPhotoOpModal(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="f-button"
                style={{ flex: 1 }}
                disabled={photoOpLoading || !getTakeoverPhotoOpSignKey() || !photoOpWindowStart || !photoOpWindowEnd || !(Number(photoOpBudget) > 0)}
                onClick={createPhotoOpLink}
              >
                {photoOpLoading ? <><FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Creating…</> : 'Create link'}
              </button>
            </div>
          </div>
        </div>
      )}
      {previewCreativeUrl && (
        <div
          className="td-modal-overlay"
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
            padding: 20
          }}
          onClick={() => setPreviewCreativeUrl('')}
        >
          <div
            className="f-card"
            style={{ maxWidth: 720, width: '100%', maxHeight: '90vh', margin: 0, padding: 0, overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--ms-border-light)' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Submitted Asset</h3>
              <button type="button" onClick={() => setPreviewCreativeUrl('')} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>
                <FaTimes />
              </button>
            </div>
            <div style={{ padding: 16, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isVideo(previewCreativeUrl) ? (
                <video src={previewCreativeUrl} controls playsInline style={{ maxWidth: '100%', maxHeight: 'calc(90vh - 72px)' }} />
              ) : (
                <img src={previewCreativeUrl} alt="Submitted asset" style={{ maxWidth: '100%', maxHeight: 'calc(90vh - 72px)', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TakeoverDetail;
